import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import { statusColors as statusColorConfig, statusLabels, getStatusStyle, getStatusColor } from '../config/statusColors';
import CreateProject from './CreateProject';
import EditProject from './EditProject';
import AdminNotificationSettings from './AdminNotificationSettings';
import NotificationManagement from './admin/NotificationManagement';
import VipDetail from './VipDetail';
import FCMTest from './FCMTest';
import AdminHelp from './AdminHelp';
import AdminFcmDevices from './AdminFcmDevices';
import AdminActivity from './AdminActivity';
import AdminAnalytics from './AdminAnalytics';
import AdminReviews from './AdminReviews';
import AdminCommissionSettings from './AdminCommissionSettings';
import AdminCommissionHistory from './AdminCommissionHistory';
import AdminProjects from './AdminProjects';
import AdminUsers from './AdminUsers';
import ContractorCommissionModal from '../components/ContractorCommissionModal';
import ContractorForm from './admin/ContractorForm';
import { 
  HomeIcon,
  DocumentDuplicateIcon,
  UserGroupIcon,
  UsersIcon,
  SparklesIcon,
  MegaphoneIcon,
  CogIcon,
  BellIcon,
  BellAlertIcon,
  Bars3Icon,
  XMarkIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PhotoIcon,
  DocumentIcon,
  QuestionMarkCircleIcon,
  ChartBarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusIcon,
  CurrencyDollarIcon,
  CalculatorIcon,
  BanknotesIcon,
  StarIcon,
  AdjustmentsHorizontalIcon,
  ClipboardDocumentListIcon,
  TrashIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';

const AdminDashboard = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { data: stats } = useQuery(
    'admin-stats',
    () => api.get('/admin/stats').then(res => res.data),
    {
      refetchInterval: 30000 // 30초마다 새로고침
    }
  );

  const navigation = [
    { name: '대시보드', href: '/admin', icon: HomeIcon, current: location.pathname === '/admin' },
    { name: '프로젝트', href: '/admin/projects', icon: DocumentDuplicateIcon, current: location.pathname.includes('/admin/projects') && location.pathname !== '/admin/projects-manage' },
    { name: '소장', href: '/admin/contractors', icon: UserGroupIcon, current: location.pathname === '/admin/contractors' },
    { name: '고객', href: '/admin/customers', icon: UsersIcon, current: location.pathname === '/admin/customers' },
    { name: 'VIP 고객', href: '/admin/vip', icon: SparklesIcon, current: location.pathname === '/admin/vip' },
    { name: 'VIP 인출요청', href: '/admin/vip/withdrawals', icon: BanknotesIcon, current: location.pathname === '/admin/vip/withdrawals' },
    { name: '수수료설정', href: '/admin/commission-settings', icon: CalculatorIcon, current: location.pathname === '/admin/commission-settings' },
    { name: '수수료내역', href: '/admin/commission-history', icon: BanknotesIcon, current: location.pathname === '/admin/commission-history' },
    { name: '평가', href: '/admin/reviews', icon: StarIcon, current: location.pathname === '/admin/reviews' },
    { name: '공지', href: '/admin/notices', icon: MegaphoneIcon, current: location.pathname === '/admin/notices' },
    { name: '도움말', href: '/admin/helps', icon: QuestionMarkCircleIcon, current: location.pathname === '/admin/helps' },
    { name: '활동기록', href: '/admin/activity', icon: ClockIcon, current: location.pathname === '/admin/activity' },
    { name: '접속분석', href: '/admin/analytics', icon: ChartBarIcon, current: location.pathname === '/admin/analytics' },
    { name: '알림설정', href: '/admin/notifications', icon: BellIcon, current: location.pathname === '/admin/notifications' },
    { name: '알림관리', href: '/admin/notification-management', icon: BellAlertIcon, current: location.pathname === '/admin/notification-management' },
    { name: 'FCM', href: '/admin/fcm-test', icon: DevicePhoneMobileIcon, current: location.pathname === '/admin/fcm-test' },
    { name: '디바이스', href: '/admin/fcm-devices', icon: DeviceTabletIcon, current: location.pathname === '/admin/fcm-devices' },
    { name: '설정', href: '/admin/settings', icon: CogIcon, current: location.pathname === '/admin/settings' }
  ];

  const dangerZone = [
    { name: '프로젝트 완전삭제', href: '/admin/projects-manage', icon: TrashIcon, current: location.pathname === '/admin/projects-manage' },
    { name: '고객 완전삭제', href: '/admin/users-manage', icon: TrashIcon, current: location.pathname === '/admin/users-manage' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* 모바일 헤더 */}
      <div className="lg:hidden bg-gradient-to-r from-blue-600 to-blue-700 shadow-lg sticky top-0 z-40">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                <span className="text-blue-600 font-bold text-lg">⚡</span>
              </div>
              <h1 className="text-xl font-bold text-white">관리자</h1>
            </div>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            >
              {mobileMenuOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* 모바일 탭 메뉴 (그리드 카드) */}
      <div className="lg:hidden bg-gray-50 px-4 py-3">
        <div className="grid grid-cols-4 gap-2">
          {navigation.slice(0, 8).map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setMobileMenuOpen(false)}
              className={`${
                item.current
                  ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              } p-3 rounded-xl flex flex-col items-center gap-1 transition-all duration-200 shadow-sm border border-gray-100`}
            >
              <item.icon className="h-6 w-6" />
              <span className="text-xs font-medium text-center leading-tight">{item.name}</span>
            </Link>
          ))}
        </div>
        
        {/* 더보기 메뉴 */}
        {navigation.length > 8 && (
          <div className="mt-3">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-full bg-white text-gray-700 p-3 rounded-xl flex items-center justify-center gap-2 shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <CogIcon className="h-5 w-5" />
              <span className="text-sm font-medium">더보기</span>
            </button>
          </div>
        )}
      </div>

      {/* 모바일 메뉴 오버레이 */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          {/* 배경 오버레이 */}
          <div 
            className="fixed inset-0 bg-gray-600 bg-opacity-75" 
            onClick={() => setMobileMenuOpen(false)}
          ></div>
          
          {/* 사이드바 */}
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:bg-gray-600"
              >
                <XMarkIcon className="h-6 w-6 text-white" />
              </button>
            </div>
            
            <div className="flex-1 h-0 pt-5 pb-20 overflow-y-auto">
              <div className="flex items-center flex-shrink-0 px-4">
                <h2 className="text-lg font-semibold text-gray-900">관리자 메뉴</h2>
              </div>
              <nav className="mt-5 px-2 space-y-1">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`${
                      item.current
                        ? 'bg-gray-100 text-gray-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    } group flex items-center px-2 py-2 text-base font-medium rounded-md`}
                  >
                    <item.icon
                      className={`${
                        item.current ? 'text-gray-500' : 'text-gray-400 group-hover:text-gray-500'
                      } mr-4 flex-shrink-0 h-6 w-6`}
                    />
                    {item.name}
                  </Link>
                ))}
              </nav>
              
              {/* 모바일 위험 영역 */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="px-4">
                  <h3 className="text-xs font-semibold text-red-600 uppercase tracking-wider mb-3">
                    위험 영역
                  </h3>
                </div>
                <nav className="px-2 space-y-1">
                  {dangerZone.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`${
                        item.current
                          ? 'bg-red-50 text-red-700'
                          : 'text-red-600 hover:bg-red-50'
                      } group flex items-center px-2 py-2 text-base font-medium rounded-md`}
                    >
                      <item.icon className="text-red-500 mr-4 flex-shrink-0 h-6 w-6" />
                      {item.name}
                    </Link>
                  ))}
                </nav>
              </div>
            </div>
          </div>
          
          <div className="flex-shrink-0 w-14">
            {/* 닫기 버튼을 위한 공간 */}
          </div>
        </div>
      )}

      <div className="flex">
        {/* PC 사이드바 */}
        <div className="hidden lg:block w-64 bg-white shadow-sm h-[calc(100vh-64px)] sticky top-16">
          <nav className="mt-8">
            <div className="px-4 space-y-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${
                    item.current
                      ? 'bg-indigo-50 text-indigo-700 border-indigo-300'
                      : 'text-gray-700 hover:bg-gray-50 border-transparent'
                  } group flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md border-l-4 transition-colors`}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.name}</span>
                </Link>
              ))}
            </div>
            
            {/* 위험 영역 - 삭제 기능 */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <div className="px-4">
                <h3 className="text-xs font-semibold text-red-600 uppercase tracking-wider mb-3">
                  위험 영역
                </h3>
                <div className="space-y-2">
                  {dangerZone.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`${
                        item.current
                          ? 'bg-red-50 text-red-700 border-red-300'
                          : 'text-red-600 hover:bg-red-50 border-transparent'
                      } group flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md border-l-4 transition-colors`}
                    >
                      <item.icon className="h-5 w-5" />
                      <span>{item.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </nav>
        </div>

        {/* 메인 콘텐츠 */}
        <div className="flex-1 p-4 lg:p-8 overflow-x-hidden">
          <Routes>
            <Route path="/" element={<DashboardHome stats={stats} />} />
            <Route path="/projects" element={<ProjectManagement />} />
            <Route path="/projects/create" element={<CreateProject />} />
            <Route path="/projects/:id/edit" element={<EditProject />} />
            <Route path="/contractors" element={<ContractorManagement />} />
            <Route path="/contractors/new" element={<ContractorForm />} />
            <Route path="/contractors/:id/edit" element={<ContractorForm />} />
            <Route path="/customers" element={<CustomerManagement />} />
            <Route path="/vip" element={<VipManagement />} />
            <Route path="/vip/withdrawals" element={<VipWithdrawals />} />
            <Route path="/vip/:id/details" element={<VipDetail />} />
            <Route path="/commission-settings" element={<AdminCommissionSettings />} />
            <Route path="/commission-history" element={<AdminCommissionHistory />} />
            <Route path="/reviews" element={<AdminReviews />} />
            <Route path="/notices" element={<NoticeManagement />} />
            <Route path="/helps" element={<AdminHelp />} />
            <Route path="/activity" element={<AdminActivity />} />
            <Route path="/analytics" element={<AdminAnalytics />} />
            <Route path="/notifications" element={<AdminNotificationSettings />} />
            <Route path="/notification-settings" element={<AdminNotificationSettings />} />
            <Route path="/notification-management" element={<NotificationManagement />} />
            <Route path="/fcm-test" element={<FCMTest />} />
            <Route path="/fcm-devices" element={<AdminFcmDevices />} />
            <Route path="/settings" element={<SystemSettings />} />
            <Route path="/projects-manage" element={<AdminProjects />} />
            <Route path="/users-manage" element={<AdminUsers />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

const DashboardHome = ({ stats }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  // 상태별 색상 매핑 (차트용)
  const statusChartColors = {
    received: getStatusColor('received', 'hex'),
    review: getStatusColor('review', 'hex'),
    contract: getStatusColor('contract', 'hex'),
    completed: getStatusColor('completed', 'hex'),
    cancelled: getStatusColor('cancelled', 'hex')
  };
  
  const statusKorean = statusLabels;
  
  // 프로젝트 상태별 개수 계산
  const projectStatusCounts = stats?.projectsByStatus?.reduce((acc, item) => {
    acc[item.status] = parseInt(item.count) || 0;
    return acc;
  }, {}) || {};
  
  // 프로젝트 상태 카드
  const projectCards = [
    { 
      name: '전체', 
      value: stats?.totalProjects || 0, 
      color: 'bg-gray-500',
      icon: '📁',
      link: '/projects'
    },
    { 
      name: '접수', 
      value: projectStatusCounts.received || 0, 
      color: 'bg-orange-500',
      icon: '📋',
      link: '/projects?filter=received'
    },
    { 
      name: '검토', 
      value: projectStatusCounts.review || 0, 
      color: 'bg-yellow-500',
      icon: '🔍',
      link: '/projects?filter=review'
    },
    { 
      name: '계약', 
      value: projectStatusCounts.contract || 0, 
      color: 'bg-purple-500',
      icon: '📝',
      link: '/projects?filter=contract'
    },
    { 
      name: '진행', 
      value: projectStatusCounts.progress || 0, 
      color: 'bg-blue-500',
      icon: '🚀',
      link: '/projects?filter=progress'
    },
    { 
      name: '완료', 
      value: projectStatusCounts.completed || 0, 
      color: 'bg-green-500',
      icon: '✅',
      link: '/projects?filter=completed'
    },
    { 
      name: '취소', 
      value: projectStatusCounts.cancelled || 0, 
      color: 'bg-red-500',
      icon: '❌',
      link: '/projects?filter=cancelled'
    }
  ];
  
  // 사용자 카드
  const userCards = [
    { 
      name: '소장', 
      value: stats?.totalContractors || 0, 
      color: 'bg-indigo-500',
      icon: '👷',
      link: '/admin/contractors'
    },
    { 
      name: '고객', 
      value: stats?.totalCustomers || 0, 
      color: 'bg-blue-500',
      icon: '👥',
      link: '/admin/customers'
    },
    { 
      name: 'VIP', 
      value: stats?.totalVipCustomers || 0, 
      color: 'bg-yellow-500',
      icon: '⭐',
      link: '/admin/vip'
    }
  ];

  // stats 데이터를 dashboardStats로 사용
  const dashboardStats = stats;

  // 휴가 중인 사용자 목록 조회
  const { data: vacationUsers, refetch: refetchVacation } = useQuery(
    'vacation-users',
    () => api.get('/users/vacation/list').then(res => res.data),
    {
      refetchInterval: 60000 // 1분마다 새로고침
    }
  );
  
  // 알림 무음 사용자 목록 조회
  const { data: notificationMutedUsers, refetch: refetchMuted } = useQuery(
    'notification-muted-users',
    () => api.get('/admin/notification-muted-users').then(res => res.data),
    {
      refetchInterval: 60000 // 1분마다 새로고침
    }
  );
  
  // VIP 인출 통계 조회
  const { data: vipWithdrawalStats } = useQuery(
    'vipWithdrawalStats',
    () => api.get('/admin/vip/withdrawal-stats').then(res => res.data),
    { 
      refetchInterval: 30000 // 30초마다 새로고침
    }
  );
  
  // 휴가 취소
  const handleCancelVacation = async (userId) => {
    if (!window.confirm('이 사용자의 휴가를 취소하시겠습니까?')) return;
    
    try {
      await api.post(`/admin/cancel-vacation/${userId}`);
      toast.success('휴가가 취소되었습니다.');
      refetchVacation();
    } catch (error) {
      console.error('휴가 취소 실패:', error);
      toast.error('휴가 취소에 실패했습니다.');
    }
  };
  
  // 알림 무음 취소
  const handleCancelNotificationMute = async (userId) => {
    if (!window.confirm('이 사용자의 알림 무음을 해제하시겠습니까?')) return;
    
    try {
      await api.post(`/admin/cancel-notification-mute/${userId}`);
      toast.success('알림 무음이 해제되었습니다.');
      refetchMuted();
    } catch (error) {
      console.error('알림 무음 해제 실패:', error);
      toast.error('알림 무음 해제에 실패했습니다.');
    }
  };

  return (
    <div className="px-4 lg:px-0">
      {/* 헤더 섹션 */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-6 lg:mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4 lg:mb-0">관리자 대시보드</h1>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => navigate('/admin/projects/create')}
            className="inline-flex items-center justify-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-xl shadow-lg text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            새 프로젝트
          </button>
          <button
            onClick={() => navigate('/admin/fcm-test')}
            className="inline-flex items-center justify-center px-4 py-2.5 border border-gray-200 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-md transition-all duration-200"
          >
            <BellIcon className="-ml-1 mr-2 h-5 w-5" />
            FCM 발송
          </button>
        </div>
      </div>
      
      {/* VIP 인출 대기 알림 */}
      {vipWithdrawalStats && vipWithdrawalStats.pendingCount > 0 && (
        <div className="mb-6">
          <div 
            onClick={() => navigate('/admin/vip/withdrawals')}
            className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-orange-500 border border-yellow-200 rounded-2xl p-6 cursor-pointer shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] animate-pulse"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-white bg-opacity-20 backdrop-blur-sm">
                    <ExclamationTriangleIcon className="h-6 w-6 text-white animate-bounce" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white drop-shadow-sm">
                    🚨 VIP 인출 요청 대기중
                  </h3>
                  <p className="text-white text-opacity-90 text-sm">
                    <span className="font-semibold">{vipWithdrawalStats.pendingCount}건</span>의 인출 요청이 처리를 기다리고 있습니다
                  </p>
                  <p className="text-white text-opacity-80 text-xs mt-1">
                    총 대기 금액: <span className="font-medium">{vipWithdrawalStats.pendingAmount?.toLocaleString()}원</span>
                    {vipWithdrawalStats.oldestHours > 0 && (
                      <span className="ml-2">
                        • 최장 대기: {Math.floor(vipWithdrawalStats.oldestHours)}시간 {Math.floor((vipWithdrawalStats.oldestHours % 1) * 60)}분
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex-shrink-0">
                <ArrowRightIcon className="h-6 w-6 text-white opacity-75 transform group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* 프로젝트 상태별 카드 */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <DocumentDuplicateIcon className="h-5 w-5 mr-2 text-blue-600" />
          프로젝트 현황
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 lg:gap-4">
          {projectCards.map((card) => (
            <div
              key={card.name}
              onClick={() => card.link && navigate(card.link)}
              className="group bg-gradient-to-br from-white to-gray-50 border border-gray-100 overflow-hidden shadow-md hover:shadow-xl rounded-2xl cursor-pointer transition-all duration-300 hover:scale-105"
            >
              <div className="p-4 lg:p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-2xl lg:text-3xl">{card.icon}</div>
                  <ArrowRightIcon className="h-4 w-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                </div>
                <p className="text-xs font-medium text-gray-500 mb-1">{card.name}</p>
                <p className="text-xl lg:text-2xl font-bold text-gray-900">{card.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* 사용자 카드 */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <UsersIcon className="h-5 w-5 mr-2 text-green-600" />
          사용자 현황
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {userCards.map((card) => (
            <div
              key={card.name}
              onClick={() => card.link && navigate(card.link)}
              className="group bg-gradient-to-br from-white to-gray-50 border border-gray-100 overflow-hidden shadow-md hover:shadow-xl rounded-2xl cursor-pointer transition-all duration-300 hover:scale-105"
            >
              <div className="p-5 lg:p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-3xl lg:text-4xl">{card.icon}</div>
                  <ArrowRightIcon className="h-5 w-5 text-gray-400 group-hover:text-green-600 transition-colors" />
                </div>
                <p className="text-sm font-medium text-gray-500 mb-2">{card.name}</p>
                <p className="text-2xl lg:text-3xl font-bold text-gray-900">{card.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 프로젝트 상태별 현황 및 최근 접속 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 mb-8">
        {/* 상태별 분포 */}
        <div className="bg-gradient-to-br from-white to-blue-50 shadow-lg rounded-2xl p-5 lg:p-6 border border-blue-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <ChartBarIcon className="h-5 w-5 mr-2 text-blue-600" />
            프로젝트 상태별 현황
          </h3>
          <div className="space-y-4">
            {Object.entries(statusKorean).map(([status, label]) => {
              const count = projectStatusCounts[status] || 0;
              const total = stats?.totalProjects || 1;
              const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
              
              return (
                <div key={status} className="bg-white/70 rounded-xl p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold text-gray-700">{label}</span>
                    <span className="text-sm font-bold text-gray-600">{count}건 ({percentage}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all duration-500 ${getStatusColor(status, 'primary')}`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* 최근 접속 사용자 */}
        <div className="bg-gradient-to-br from-white to-green-50 shadow-lg rounded-2xl p-5 lg:p-6 border border-green-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <ClockIcon className="h-5 w-5 mr-2 text-green-600" />
            최근 접속
          </h3>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {stats?.recentAccessUsers?.slice(0, 10).map((user) => {
              const timeDiff = new Date() - new Date(user.last_access);
              const minutes = Math.floor(timeDiff / 60000);
              const hours = Math.floor(minutes / 60);
              const days = Math.floor(hours / 24);
              
              let timeAgo = '';
              if (days > 0) {
                timeAgo = `${days}일 전`;
              } else if (hours > 0) {
                timeAgo = `${hours}시간 전`;
              } else if (minutes > 0) {
                timeAgo = `${minutes}분 전`;
              } else {
                timeAgo = '방금 전';
              }
              
              const userTypeKorean = {
                admin: '관리자',
                contractor: '소장',
                customer: '고객',
                vip_customer: 'VIP'
              };
              
              const userTypeColor = {
                admin: 'bg-purple-100 text-purple-800',
                contractor: 'bg-blue-100 text-blue-800',
                customer: 'bg-gray-100 text-gray-800',
                vip_customer: 'bg-yellow-100 text-yellow-800'
              };
              
              return (
                <div key={user.id} className="bg-white/70 rounded-xl p-3 hover:bg-white transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center">
                          <span className="text-sm font-bold text-gray-700">
                            {user.name.charAt(0)}
                          </span>
                        </div>
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                          <span className="text-sm font-semibold text-gray-900 truncate">{user.name}</span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${userTypeColor[user.user_type]}`}>
                            {userTypeKorean[user.user_type]}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 truncate">{user.email || user.phone}</p>
                      </div>
                    </div>
                    <div className="text-xs font-medium text-gray-500 whitespace-nowrap">
                      {timeAgo}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* 소장별 현황과 실시간 정보 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6 mb-8">
        {/* 소장별 업무 현황 */}
        <div className="bg-gradient-to-br from-white to-purple-50 shadow-lg rounded-2xl p-5 lg:p-6 border border-purple-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <UserGroupIcon className="h-5 w-5 mr-2 text-purple-600" />
            소장별 진행 현황
          </h3>
          <div className="space-y-3">
            {stats?.contractorStats?.slice(0, 5).map((contractor) => (
              <div key={contractor.contractor_id} className="bg-white/70 rounded-xl p-4 hover:bg-white transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-gray-900 truncate">
                      {contractor.contractor?.name || '-'}
                    </h4>
                    {contractor.contractor?.company_name && (
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {contractor.contractor.company_name}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-4 ml-3">
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-600">{contractor.project_count || 0}</div>
                      <div className="text-xs text-gray-500">진행중</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-600">{contractor.monthly_completed || 0}</div>
                      <div className="text-xs text-gray-500">완료</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {stats?.contractorStats?.length > 5 && (
              <div className="mt-4 text-center">
                <Link
                  to="/admin/contractors"
                  className="inline-flex items-center gap-1 text-sm font-medium text-purple-600 hover:text-purple-700 transition-colors"
                >
                  전체 보기
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* 실시간 활동 */}
        <div className="bg-gradient-to-br from-white to-orange-50 shadow-lg rounded-2xl p-5 lg:p-6 border border-orange-100 lg:col-span-2 xl:col-span-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <BellIcon className="h-5 w-5 mr-2 text-orange-600" />
            최근 활동
          </h3>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {stats?.criticalActivities?.slice(0, 10).map((activity, index) => {
              // description에서 이름 제거
              let cleanDescription = '';
              if (activity.action_type === 'project_status_change') {
                const oldStatus = statusKorean[activity.old_value] || activity.old_value;
                const newStatus = statusKorean[activity.new_value] || activity.new_value;
                cleanDescription = `${oldStatus} → ${newStatus}`;
              } else if (activity.action_type === 'vip_conversion') {
                cleanDescription = `VIP 전환`;
              } else if (activity.action_type === 'project_cancelled') {
                cleanDescription = `프로젝트 취소`;
              } else if (activity.action_type === 'withdrawal_request') {
                cleanDescription = `출금 요청`;
              }
              
              return (
                <div key={index} className="bg-white/70 rounded-xl p-3 hover:bg-white transition-colors">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {activity.action_type === 'project_status_change' && (
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <ArrowRightIcon className="w-4 h-4 text-blue-600" />
                        </div>
                      )}
                      {activity.action_type === 'vip_conversion' && (
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                          <SparklesIcon className="w-4 h-4 text-purple-600" />
                        </div>
                      )}
                      {activity.action_type === 'project_cancelled' && (
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                          <XCircleIcon className="w-4 h-4 text-red-600" />
                        </div>
                      )}
                      {activity.action_type === 'withdrawal_request' && (
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <CurrencyDollarIcon className="w-4 h-4 text-green-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {cleanDescription}
                      </p>
                      {activity.project_title && (
                        <p className="text-xs text-gray-600 font-medium mt-1 truncate">
                          {activity.project_title}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(activity.created_at).toLocaleTimeString('ko-KR', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 오늘의 통계 및 경고 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 mb-8">
        {/* 경고 사항 */}
        <div className="bg-gradient-to-br from-white to-red-50 shadow-lg rounded-2xl p-5 lg:p-6 border border-red-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 mr-2 text-red-600" />
            주의 사항
          </h3>
          <div className="space-y-4">
            {stats?.unassignedProjects > 0 && (
              <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200 rounded-xl p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-semibold text-yellow-800">
                      미배정 프로젝트 {stats.unassignedProjects}건
                    </p>
                    <p className="text-xs text-yellow-600 mt-1">소장 배정이 필요합니다</p>
                  </div>
                </div>
              </div>
            )}
            {stats?.longRunningProjects > 0 && (
              <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ClockIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-semibold text-red-800">
                      장기 진행 프로젝트 {stats.longRunningProjects}건
                    </p>
                    <p className="text-xs text-red-600 mt-1">30일 이상 진행 중</p>
                  </div>
                </div>
              </div>
            )}
            {projectStatusCounts.received > 5 && (
              <div className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <DocumentIcon className="h-6 w-6 text-orange-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-semibold text-orange-800">
                      접수 대기 {projectStatusCounts.received}건
                    </p>
                    <p className="text-xs text-orange-600 mt-1">신속한 처리가 필요합니다</p>
                  </div>
                </div>
              </div>
            )}
            {!stats?.unassignedProjects && !stats?.longRunningProjects && !(projectStatusCounts.received > 5) && (
              <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-xl p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-semibold text-green-800">
                      모든 프로젝트가 정상 진행 중입니다
                    </p>
                    <p className="text-xs text-green-600 mt-1">특별한 주의사항이 없습니다</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 통계 차트 */}
        <div className="bg-gradient-to-br from-white to-indigo-50 shadow-lg rounded-2xl p-5 lg:p-6 border border-indigo-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <ChartBarIcon className="h-5 w-5 mr-2 text-indigo-600" />
            이번 달 통계
          </h3>
          {stats?.projectsByStatus && stats.projectsByStatus.length > 0 ? (
            <div className="bg-white/70 rounded-xl p-4">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={stats.projectsByStatus.map(item => ({
                      name: statusKorean[item.status] || item.status,
                      value: parseInt(item.count) || 0
                    }))}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => percent > 5 ? `${name} ${(percent * 100).toFixed(0)}%` : ''}
                    outerRadius={window.innerWidth < 768 ? 70 : 90}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {stats.projectsByStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={statusChartColors[entry.status] || '#8884d8'} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [`${value}건`, name]}
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="bg-white/70 rounded-xl p-8 text-center">
              <div className="text-gray-400 mb-2">
                <ChartBarIcon className="h-12 w-12 mx-auto" />
              </div>
              <p className="text-sm text-gray-500">표시할 데이터가 없습니다</p>
            </div>
          )}
        </div>
      </div>

      {/* 휴가 및 알림 무음 사용자 */}
      {((vacationUsers && vacationUsers.length > 0) || (notificationMutedUsers && notificationMutedUsers.length > 0)) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* 휴가 중인 사용자 */}
          {vacationUsers && vacationUsers.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <span className="text-2xl mr-2">🏖️</span>
                휴가 중인 사용자
              </h3>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {vacationUsers.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-900">{user.name}</span>
                        <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          user.user_type === 'admin' ? 'bg-purple-100 text-purple-800' : 
                          user.user_type === 'contractor' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {user.user_type === 'admin' ? '관리자' : 
                           user.user_type === 'contractor' ? '소장' : '고객'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {new Date(user.vacation_start_date).toLocaleDateString()} ~ 
                        {new Date(user.vacation_end_date).toLocaleDateString()}
                        <span className="ml-2 font-medium text-blue-600">
                          ({user.days_remaining || 0}일 남음)
                        </span>
                      </div>
                      {user.vacation_reason && (
                        <div className="text-xs text-gray-500 mt-1">{user.vacation_reason}</div>
                      )}
                    </div>
                    <button
                      onClick={() => handleCancelVacation(user.id)}
                      className="ml-4 text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      취소
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* 알림 무음 사용자 */}
          {notificationMutedUsers && notificationMutedUsers.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <span className="text-2xl mr-2">🔕</span>
                알림 무음 사용자
              </h3>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {notificationMutedUsers.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-900">{user.name}</span>
                        <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          user.user_type === 'admin' ? 'bg-purple-100 text-purple-800' : 
                          user.user_type === 'contractor' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {user.user_type === 'admin' ? '관리자' : 
                           user.user_type === 'contractor' ? '소장' : '고객'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        종료: {new Date(user.notification_muted_until).toLocaleString()}
                        <span className="ml-2 font-medium text-orange-600">
                          ({Math.max(0, Math.floor(user.remaining_minutes / 60))}시간 남음)
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCancelNotificationMute(user.id)}
                      className="ml-4 text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      해제
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* 최근 중요 활동 */}
      {dashboardStats?.criticalActivities && dashboardStats.criticalActivities.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            🔔 최근 중요 활동
          </h2>
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="divide-y divide-gray-200">
              {dashboardStats.criticalActivities.map((activity) => (
                <div key={activity.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          activity.action_type === 'project_status_change' ? 'bg-blue-100 text-blue-800' :
                          activity.action_type === 'vip_conversion' ? 'bg-purple-100 text-purple-800' :
                          activity.action_type === 'project_cancelled' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {activity.action_type === 'project_status_change' ? '상태변경' :
                           activity.action_type === 'vip_conversion' ? 'VIP전환' :
                           activity.action_type === 'project_cancelled' ? '프로젝트취소' :
                           '출금요청'}
                        </span>
                        <span className="ml-2 text-sm font-medium text-gray-900">
                          {activity.user_name} ({activity.user_type})
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-gray-500">
                        {activity.description}
                        {activity.project_title && (
                          <span className="ml-2">- 프로젝트: {activity.project_title}</span>
                        )}
                      </div>
                    </div>
                    <div className="ml-4 text-xs text-gray-500">
                      {new Date(activity.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ProjectManagement = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = React.useState('all');
  const [contractorFilter, setContractorFilter] = React.useState('');
  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25); // 기본값 25로 변경
  const [editingPassword, setEditingPassword] = React.useState(null);
  const [newPassword, setNewPassword] = React.useState('');
  const [showQuickStatus, setShowQuickStatus] = React.useState(null);
  const [viewLogsModal, setViewLogsModal] = React.useState({
    show: false,
    projectId: null,
    userId: null,
    userName: '',
    userType: ''
  });
  
  // 소장 목록 가져오기
  const { data: contractorsData } = useQuery(
    'admin-contractors',
    () => api.get('/admin/contractors').then(res => res.data)
  );
  
  const contractors = contractorsData?.contractors || [];

  const { data: projects, isLoading, refetch } = useQuery(
    ['admin-projects', filter, contractorFilter, dateFrom, dateTo, search, page, pageSize],
    () => api.get('/admin/projects', {
      params: { 
        status: filter !== 'all' ? filter : undefined, 
        contractor_id: contractorFilter || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        search, 
        page, 
        limit: pageSize 
      }
    }).then(res => res.data),
    { keepPreviousData: true }
  );

  // 조회 이력 조회
  const { data: viewLogs } = useQuery(
    ['viewLogs', viewLogsModal.projectId, viewLogsModal.userId],
    () => api.get(`/projects/${viewLogsModal.projectId}/view-logs/${viewLogsModal.userId}`).then(res => res.data),
    {
      enabled: viewLogsModal.show && !!viewLogsModal.projectId && !!viewLogsModal.userId,
      staleTime: 0
    }
  );

  const handlePasswordUpdate = async (projectId) => {
    try {
      await api.put(`/projects/${projectId}`, {
        customer_password: newPassword
      });
      setEditingPassword(null);
      setNewPassword('');
      refetch();
      alert('비밀번호가 변경되었습니다.');
    } catch (error) {
      console.error('비밀번호 변경 실패:', error);
      alert('비밀번호 변경에 실패했습니다.');
    }
  };
  
  // 빠른 상태 변경 기능
  const handleQuickStatusChange = async (projectId, newStatus) => {
    try {
      await api.put(`/projects/${projectId}`, {
        status: newStatus
      });
      toast.success('상태가 변경되었습니다.');
      refetch();
      setShowQuickStatus(null);
    } catch (error) {
      console.error('상태 변경 실패:', error);
      toast.error('상태 변경에 실패했습니다.');
    }
  };

  const startPasswordEdit = (project) => {
    setEditingPassword(project.id);
    setNewPassword(project.customer_password || '');
  };

  const cancelPasswordEdit = () => {
    setEditingPassword(null);
    setNewPassword('');
  };

  // statusLabels는 이미 import 되어 있음

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl lg:text-2xl font-semibold text-gray-900">프로젝트 관리</h1>
        <Link
          to="/admin/projects/create"
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap"
        >
          새 프로젝트
        </Link>
      </div>

      {/* 필터 및 검색 */}
      <div className="bg-white shadow rounded-lg p-4 lg:p-6 mb-6">
        <div className="space-y-4">
          {/* 첫번째 줄: 상태 필터 및 미배정 버튼 */}
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={filter}
              onChange={(e) => { setFilter(e.target.value); setPage(1); }}
              className="block w-full sm:w-auto px-3 py-2 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
            >
              <option value="all">전체 상태</option>
              <option value="received">접수</option>
              <option value="review">검토</option>
              <option value="contract">계약</option>
              <option value="progress">진행</option>
              <option value="completed">완료</option>
              <option value="cancelled">취소</option>
            </select>
            <button
              onClick={() => { setFilter(filter === 'unassigned' ? 'all' : 'unassigned'); setPage(1); }}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                filter === 'unassigned'
                  ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🚨 미배정 프로젝트만 보기
            </button>
            <select
              value={contractorFilter}
              onChange={(e) => { setContractorFilter(e.target.value); setPage(1); }}
              className="block w-full sm:w-auto px-3 py-2 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
            >
              <option value="">전체 소장</option>
              {contractors?.map(contractor => (
                <option key={contractor.id} value={contractor.id}>
                  {contractor.name} {contractor.company_name && `(${contractor.company_name})`}
                </option>
              ))}
            </select>
          </div>
          
          {/* 두번째 줄: 날짜 필터 및 검색 */}
          <div className="flex flex-col lg:flex-row gap-2">
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                className="block w-full sm:w-auto px-3 py-2 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
                placeholder="시작일"
              />
              <span className="self-center text-gray-500 hidden sm:inline">~</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                className="block w-full sm:w-auto px-3 py-2 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
                placeholder="종료일"
              />
              {(dateFrom || dateTo) && (
                <button
                  onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); }}
                  className="px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  날짜 초기화
                </button>
              )}
            </div>
            <input
              type="text"
              placeholder="프로젝트명, 주소, 고객명, 소장명 검색..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block w-full lg:flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          </div>
          
          {/* 세번째 줄: 엑셀 다운로드 및 요약 정보 */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                총 <span className="font-semibold">{projects?.total || 0}</span>개 프로젝트
                {contractorFilter && contractors && (
                  <span className="ml-2">
                    (소장: {contractors.find(c => c.id === parseInt(contractorFilter))?.name})
                  </span>
                )}
                {(dateFrom || dateTo) && (
                  <span className="ml-2">
                    ({dateFrom || '처음'}부터 {dateTo || '현재'}까지)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">페이지당:</span>
                <select
                  value={pageSize}
                  onChange={(e) => { 
                    setPageSize(parseInt(e.target.value)); 
                    setPage(1); 
                  }}
                  className="px-2 py-1 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
                >
                  <option value="25">25개</option>
                  <option value="50">50개</option>
                  <option value="100">100개</option>
                  <option value="200">200개</option>
                </select>
              </div>
            </div>
            <button
              onClick={async () => {
                try {
                  const response = await api.get('/admin/projects/export', {
                    params: {
                      status: filter !== 'all' ? filter : undefined,
                      contractor_id: contractorFilter || undefined,
                      date_from: dateFrom || undefined,
                      date_to: dateTo || undefined,
                      search
                    },
                    responseType: 'blob'
                  });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `projects_${new Date().toISOString().split('T')[0]}.xlsx`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  toast.success('엑셀 파일이 다운로드되었습니다.');
                } catch (error) {
                  toast.error('엑셀 다운로드에 실패했습니다.');
                }
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
            >
              📊 엑셀 다운로드
            </button>
          </div>
        </div>
      </div>

      {/* 프로젝트 목록 */}
      <div className="bg-white shadow rounded-lg">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">로딩 중...</p>
          </div>
        ) : (
          <>
            {/* 모바일/태블릿: 카드 레이아웃 */}
            <div className="xl:hidden space-y-3 p-4">
              {projects?.projects?.map((project) => (
                <div key={project.id} className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
                <div className="flex justify-between items-start mb-3 gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 truncate" title={project.title}>{project.title}</h3>
                    <p className="text-xs text-gray-500 mt-1 truncate" title={project.construction_types}>{project.construction_types}</p>
                  </div>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${getStatusStyle(project.status, 'medium')}`}>
                    {statusLabels[project.status]}
                  </span>
                </div>
                
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">고객:</span>
                    <div className="flex items-center gap-1">
                      {project.customer_view_count !== undefined && (
                        <button
                          onClick={() => {
                            setViewLogsModal({
                              show: true,
                              projectId: project.id,
                              userId: project.customer_id,
                              userName: project.customer?.name,
                              userType: 'customer'
                            });
                          }}
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                            project.customer_view_count > 0 
                              ? (project.customer_view_count >= 4 ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-blue-100 text-blue-700 hover:bg-blue-200')
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                          title="클릭하여 조회 이력 보기"
                        >
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                            project.customer_view_count > 0 
                              ? (project.customer_view_count >= 4 ? 'bg-green-500' : 'bg-blue-500')
                              : 'bg-gray-400'
                          }`}></span>
                          <span>{project.customer_view_count || 0}</span>
                        </button>
                      )}
                      <span className="text-gray-900">{project.customer?.name} ({project.customer?.phone})</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">소장:</span>
                    <div className="flex items-center gap-1">
                      {project.contractor_view_count !== undefined && project.contractor?.name && (
                        <button
                          onClick={() => {
                            setViewLogsModal({
                              show: true,
                              projectId: project.id,
                              userId: project.contractor_id,
                              userName: project.contractor?.name,
                              userType: 'contractor'
                            });
                          }}
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                            project.contractor_view_count > 0 
                              ? (project.contractor_view_count >= 4 ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-blue-100 text-blue-700 hover:bg-blue-200')
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                          title="클릭하여 조회 이력 보기"
                        >
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                            project.contractor_view_count > 0 
                              ? (project.contractor_view_count >= 4 ? 'bg-green-500' : 'bg-blue-500')
                              : 'bg-gray-400'
                          }`}></span>
                          <span>{project.contractor_view_count || 0}</span>
                        </button>
                      )}
                      <span className="text-gray-900">{project.contractor?.name || '미배정'}</span>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">금액:</span>
                    <span className="text-gray-900">
                      {project.contract_amount ? `${project.contract_amount.toLocaleString()}원` : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">비밀번호:</span>
                    {editingPassword === project.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-20 px-2 py-1 text-xs border rounded"
                        />
                        <button
                          onClick={() => handlePasswordUpdate(project.id)}
                          className="text-green-600 hover:text-green-800"
                        >
                          ✓
                        </button>
                        <button
                          onClick={cancelPasswordEdit}
                          className="text-red-600 hover:text-red-800"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => startPasswordEdit(project)}
                        className="font-mono text-indigo-600 hover:text-indigo-800"
                      >
                        {project.customer_password || '설정'}
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="space-y-2 mt-3 pt-3 border-t">
                  {/* 프로젝트 관리 버튼 */}
                  <div className="text-xs text-gray-500 font-medium">관리</div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        // 프로젝트 캐시 무효화
                        queryClient.invalidateQueries(['project', project.id.toString()]);
                        navigate(`/admin/projects/${project.id}/edit`);
                      }}
                      className="flex-1 text-center px-2 py-1.5 bg-indigo-50 text-indigo-700 text-xs font-medium rounded hover:bg-indigo-100"
                    >
                      수정
                    </button>
                    <Link
                      to={`/projects/${project.id}`}
                      className="flex-1 text-center px-2 py-1.5 bg-gray-50 text-gray-700 text-xs font-medium rounded hover:bg-gray-100"
                    >
                      상세보기
                    </Link>
                  </div>
                  {/* 고객 URL 복사 버튼 */}
                  {project.customer_password && (
                    <>
                      <div className="text-xs text-gray-500 font-medium mt-2">고객 복사</div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            const url = `${window.location.origin}/h/${project.id}/${project.customer_password}`;
                            navigator.clipboard.writeText(url);
                            toast.success('고객 홈 URL이 복사되었습니다.');
                          }}
                          className="flex-1 px-1.5 py-1 bg-green-50 text-green-700 text-xs font-medium rounded hover:bg-green-100"
                          title="홈 URL 복사"
                        >
                          홈
                        </button>
                        <button
                          onClick={() => {
                            const url = `${window.location.origin}/${project.id}/${project.customer_password}`;
                            navigator.clipboard.writeText(url);
                            toast.success('고객 정보 URL이 복사되었습니다.');
                          }}
                          className="flex-1 px-1.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded hover:bg-blue-100"
                          title="정보 URL 복사"
                        >
                          정보
                        </button>
                        <button
                          onClick={() => {
                            const url = `${window.location.origin}/s/${project.id}/${project.customer_password}`;
                            navigator.clipboard.writeText(url);
                            toast.success('고객 소통 URL이 복사되었습니다.');
                          }}
                          className="flex-1 px-1.5 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded hover:bg-purple-100"
                          title="소통 URL 복사"
                        >
                          소통
                        </button>
                      </div>
                    </>
                  )}
                  
                  {/* 소장 URL 복사 버튼 */}
                  {project.contractor && (
                    <>
                      <div className="text-xs text-gray-500 font-medium mt-2">소장 복사</div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            const url = `${window.location.origin}/p/${project.id}`;
                            navigator.clipboard.writeText(url);
                            toast.success('소장 홈 URL이 복사되었습니다.');
                          }}
                          className="flex-1 px-1.5 py-1 bg-green-50 text-green-700 text-xs font-medium rounded hover:bg-green-100"
                          title="홈 URL 복사"
                        >
                          홈
                        </button>
                        <button
                          onClick={() => {
                            const url = `${window.location.origin}/p/${project.id}`;
                            navigator.clipboard.writeText(url);
                            toast.success('소장 정보 URL이 복사되었습니다.');
                          }}
                          className="flex-1 px-1.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded hover:bg-blue-100"
                          title="정보 URL 복사"
                        >
                          정보
                        </button>
                        <button
                          onClick={() => {
                            const url = `${window.location.origin}/s/${project.id}`;
                            navigator.clipboard.writeText(url);
                            toast.success('소장 소통 URL이 복사되었습니다.');
                          }}
                          className="flex-1 px-1.5 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded hover:bg-purple-100"
                          title="소통 URL 복사"
                        >
                          소통
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* PC: 테이블 레이아웃 */}
          <div className="hidden xl:block">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    프로젝트
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    고객
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    소장
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    금액
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    비밀번호
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    등록일
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    액션
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {projects?.projects?.map((project) => (
                  <tr key={project.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="max-w-[250px]">
                        <div className="text-sm font-medium text-gray-900 truncate" title={project.title}>
                          {project.title}
                        </div>
                        <div className="text-xs text-gray-500 truncate" title={project.construction_types}>
                          {project.construction_types}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="max-w-[150px]">
                          <div className="text-sm text-gray-900 truncate flex items-center gap-1" title={project.customer?.name}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setViewLogsModal({
                                  show: true,
                                  projectId: project.id,
                                  userId: project.customer_id,
                                  userName: project.customer?.name,
                                  userType: 'customer'
                                });
                              }}
                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                                project.customer_view_count > 0 
                                  ? (project.customer_view_count >= 4 ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-blue-100 text-blue-700 hover:bg-blue-200')
                                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                              }`}
                              title="클릭하여 조회 이력 보기"
                            >
                              <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                                project.customer_view_count > 0 
                                  ? (project.customer_view_count >= 4 ? 'bg-green-500' : 'bg-blue-500')
                                  : 'bg-gray-400'
                              }`}></span>
                              <span>{project.customer_view_count || 0}</span>
                            </button>
                            {project.customer?.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {project.customer?.phone}
                          </div>
                        </div>
                        {/* 고객 URL 복사 버튼 */}
                        {project.customer_password && (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-500">복사</span>
                            <button
                              onClick={() => {
                                const url = `${window.location.origin}/h/${project.id}/${project.customer_password}`;
                                navigator.clipboard.writeText(url);
                                toast.success('고객 홈 URL이 복사되었습니다.');
                              }}
                              className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors text-xs"
                              title="홈"
                            >
                              홈
                            </button>
                            <button
                              onClick={() => {
                                const url = `${window.location.origin}/${project.id}/${project.customer_password}`;
                                navigator.clipboard.writeText(url);
                                toast.success('고객 정보 URL이 복사되었습니다.');
                              }}
                              className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors text-xs"
                              title="정보"
                            >
                              정보
                            </button>
                            <button
                              onClick={() => {
                                const url = `${window.location.origin}/s/${project.id}/${project.customer_password}`;
                                navigator.clipboard.writeText(url);
                                toast.success('고객 소통 URL이 복사되었습니다.');
                              }}
                              className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors text-xs"
                              title="소통"
                            >
                              소통
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="max-w-[120px]">
                          <div className="text-sm text-gray-900 truncate flex items-center gap-1" title={project.contractor?.name || '미배정'}>
                            {project.contractor && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setViewLogsModal({
                                    show: true,
                                    projectId: project.id,
                                    userId: project.contractor_id,
                                    userName: project.contractor?.name,
                                    userType: 'contractor'
                                  });
                                }}
                                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                                  project.contractor_view_count > 0 
                                    ? (project.contractor_view_count >= 4 ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-blue-100 text-blue-700 hover:bg-blue-200')
                                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                }`}
                                title="클릭하여 조회 이력 보기"
                              >
                                <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                                  project.contractor_view_count > 0 
                                    ? (project.contractor_view_count >= 4 ? 'bg-green-500' : 'bg-blue-500')
                                    : 'bg-gray-400'
                                }`}></span>
                                <span>{project.contractor_view_count || 0}</span>
                              </button>
                            )}
                            {project.contractor?.name || '미배정'}
                          </div>
                          <div className="text-xs text-gray-500 truncate" title={project.contractor?.company_name}>
                            {project.contractor?.company_name}
                          </div>
                        </div>
                        {/* 소장 URL 복사 버튼 */}
                        {project.contractor && (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-500">복사</span>
                            <button
                              onClick={() => {
                                const url = `${window.location.origin}/p/${project.id}`;
                                navigator.clipboard.writeText(url);
                                toast.success('소장 홈 URL이 복사되었습니다.');
                              }}
                              className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors text-xs"
                              title="홈"
                            >
                              홈
                            </button>
                            <button
                              onClick={() => {
                                const url = `${window.location.origin}/p/${project.id}`;
                                navigator.clipboard.writeText(url);
                                toast.success('소장 정보 URL이 복사되었습니다.');
                              }}
                              className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors text-xs"
                              title="정보"
                            >
                              정보
                            </button>
                            <button
                              onClick={() => {
                                const url = `${window.location.origin}/s/${project.id}`;
                                navigator.clipboard.writeText(url);
                                toast.success('소장 소통 URL이 복사되었습니다.');
                              }}
                              className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors text-xs"
                              title="소통"
                            >
                              소통
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {showQuickStatus === project.id ? (
                        <select
                          value={project.status}
                          onChange={(e) => handleQuickStatusChange(project.id, e.target.value)}
                          onBlur={() => setShowQuickStatus(null)}
                          className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          autoFocus
                        >
                          <option value="received">접수</option>
                          <option value="review">검토</option>
                          <option value="contract">계약</option>
                          <option value="progress">진행</option>
                          <option value="completed">완료</option>
                          <option value="cancelled">취소</option>
                        </select>
                      ) : (
                        <button
                          onClick={() => setShowQuickStatus(project.id)}
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full hover:opacity-80 transition-opacity ${getStatusStyle(project.status, 'medium')}`}
                        >
                          {statusLabels[project.status]}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      <div className="text-right">
                        {project.contract_amount ? (
                          <div>
                            <div className="font-medium text-sm">{(project.contract_amount/10000).toLocaleString()}만원</div>
                            {project.estimated_amount && project.estimated_amount !== project.contract_amount && (
                              <div className="text-xs text-gray-500 line-through">{(project.estimated_amount/10000).toLocaleString()}만원</div>
                            )}
                          </div>
                        ) : project.estimated_amount ? (
                          <div className="text-gray-500 text-sm">{(project.estimated_amount/10000).toLocaleString()}만원</div>
                        ) : (
                          '-'
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {editingPassword === project.id ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            maxLength="6"
                          />
                          <button
                            onClick={() => handlePasswordUpdate(project.id)}
                            className="text-green-600 hover:text-green-900 text-xs"
                          >
                            저장
                          </button>
                          <button
                            onClick={cancelPasswordEdit}
                            className="text-gray-600 hover:text-gray-900 text-xs"
                          >
                            취소
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                            {project.customer_password || '없음'}
                          </span>
                          <button
                            onClick={() => startPasswordEdit(project)}
                            className="text-indigo-600 hover:text-indigo-900 text-xs"
                          >
                            변경
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                      {new Date(project.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => {
                            // 프로젝트 캐시 무효화
                            queryClient.invalidateQueries(['project', project.id.toString()]);
                            navigate(`/admin/projects/${project.id}/edit`);
                          }}
                          className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 transition-colors"
                          title="수정"
                        >
                          수정
                        </button>
                        <Link
                          to={`/projects/${project.id}`}
                          className="px-3 py-1 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                          title="상세보기"
                        >
                          상세
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </div>
          </>
        )}

        {/* 페이지네이션 */}
        {projects?.pages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
            <div className="flex flex-1 justify-between sm:hidden">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                이전
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === projects.pages}
                className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                다음
              </button>
            </div>
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  총 <span className="font-medium">{projects.total}</span>개 중{' '}
                  <span className="font-medium">{(page - 1) * pageSize + 1}</span>-
                  <span className="font-medium">{Math.min(page * pageSize, projects.total)}</span>개 표시
                </p>
              </div>
              <div>
                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  
                  {/* 페이지 번호 표시 로직 개선 */}
                  {(() => {
                    const totalPages = projects.pages;
                    const maxButtons = 7; // 표시할 최대 버튼 수
                    let startPage = Math.max(1, page - Math.floor(maxButtons / 2));
                    let endPage = Math.min(totalPages, startPage + maxButtons - 1);
                    
                    if (endPage - startPage < maxButtons - 1) {
                      startPage = Math.max(1, endPage - maxButtons + 1);
                    }
                    
                    const pageNumbers = [];
                    
                    // 첫 페이지
                    if (startPage > 1) {
                      pageNumbers.push(
                        <button
                          key={1}
                          onClick={() => setPage(1)}
                          className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                        >
                          1
                        </button>
                      );
                      
                      if (startPage > 2) {
                        pageNumbers.push(
                          <span key="dots1" className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 bg-white">
                            ...
                          </span>
                        );
                      }
                    }
                    
                    // 중간 페이지들
                    for (let i = startPage; i <= endPage; i++) {
                      pageNumbers.push(
                        <button
                          key={i}
                          onClick={() => setPage(i)}
                          className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                            page === i
                              ? 'z-10 bg-indigo-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                              : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    }
                    
                    // 마지막 페이지
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pageNumbers.push(
                          <span key="dots2" className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 bg-white">
                            ...
                          </span>
                        );
                      }
                      
                      pageNumbers.push(
                        <button
                          key={totalPages}
                          onClick={() => setPage(totalPages)}
                          className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                        >
                          {totalPages}
                        </button>
                      );
                    }
                    
                    return pageNumbers;
                  })()}
                  
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === projects.pages}
                    className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* 조회 이력 모달 */}
      {viewLogsModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b">
              <h3 className="text-lg font-semibold">
                {viewLogsModal.userName} {viewLogsModal.userType === 'customer' ? '고객님' : '소장님'} 조회 이력
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                총 {viewLogs?.length || 0}회 조회
              </p>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {viewLogs && viewLogs.length > 0 ? (
                <div className="space-y-3">
                  {viewLogs.map((log, index) => (
                    <div key={log.id} className="flex items-start gap-3 pb-3 border-b last:border-0">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium text-gray-600">
                          {index + 1}
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {log.formatted_time}
                        </p>
                        <p className="text-xs text-gray-500">
                          {log.relative_time}
                        </p>
                        {log.user_agent && (
                          <p className="text-xs text-gray-400 mt-1 truncate">
                            {log.user_agent.includes('Mobile') ? '📱 모바일' : '💻 데스크톱'}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">
                  조회 이력이 없습니다.
                </p>
              )}
            </div>
            
            <div className="p-4 border-t">
              <button
                onClick={() => setViewLogsModal({
                  show: false,
                  projectId: null,
                  userId: null,
                  userName: '',
                  userType: ''
                })}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ContractorManagement = () => {
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25); // 기본값 25로 변경
  const [activeTab, setActiveTab] = React.useState('active'); // 'active', 'inactive', 'ios', 'all'
  const [commissionModalContractor, setCommissionModalContractor] = React.useState(null);
  const [editingIosUser, setEditingIosUser] = React.useState(null); // iOS 사용자 편집 상태

  const { data: contractorsData, isLoading, refetch } = useQuery(
    ['admin-contractors', search, page, pageSize, activeTab],
    () => {
      const params = { 
        search, 
        page, 
        limit: pageSize
      };
      
      // activeTab에 따라 필터 파라미터 설정
      if (activeTab === 'active') {
        params.is_active = 'true';
      } else if (activeTab === 'inactive') {
        params.is_active = 'false';
      } else if (activeTab === 'ios') {
        params.is_ios = 'true';
      }
      // 'all'인 경우 파라미터를 포함하지 않음
      
      return api.get('/admin/contractors', { params }).then(res => res.data);
    }
  );
  
  const contractors = contractorsData?.contractors || contractorsData || [];

  const handleToggleActive = async (id, isActive) => {
    try {
      await api.patch(`/admin/contractors/${id}/toggle-active`, { is_active: !isActive });
      refetch();
    } catch (error) {
      console.error('소장 활성화 상태 변경 실패:', error);
    }
  };

  const handleToggleIosUser = async (contractor) => {
    try {
      console.log('handleToggleIosUser called:', contractor);
      console.log('Current is_ios_user status:', contractor.is_ios_user);
      
      const newIosStatus = contractor.is_ios_user ? 0 : 1;
      console.log('New iOS status will be:', newIosStatus);
      
      const updateData = { 
        name: contractor.name,
        login_id: contractor.login_id || '',
        email: contractor.email || '',
        phone: contractor.phone || '',
        company_name: contractor.company_name || '',
        specialties: contractor.specialties || '',
        admin_notes: contractor.admin_notes || '',
        is_ios_user: newIosStatus
      };
      
      console.log('Sending update data:', updateData);
      
      const response = await api.put(`/admin/contractors/${contractor.id}`, updateData);
      console.log('Update response:', response.data);
      
      await refetch();
      console.log(`iOS 사용자 상태 변경 완료: ${contractor.name} -> ${newIosStatus ? 'iOS 사용자' : '일반 사용자'}`);
      
      // 성공 알림 추가
      alert(`${contractor.name}님이 ${newIosStatus ? 'iOS 사용자로 설정' : 'iOS 사용자 해제'}되었습니다.`);
    } catch (error) {
      console.error('iOS 사용자 상태 변경 실패 - Full error:', error);
      console.error('Error response:', error.response);
      alert(`iOS 사용자 상태 변경에 실패했습니다.\n${error.response?.data?.error || error.message}`);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl lg:text-2xl font-semibold text-gray-900">소장 관리</h1>
        <div className="flex gap-2">
          <Link
            to="/admin/contractor-applications"
            className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap inline-block"
          >
            입점 신청 관리
          </Link>
          <a
            href="/contractor/apply"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap inline-block"
          >
            입점 신청 페이지
          </a>
          <Link
            to="/admin/contractors/new"
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap inline-block"
          >
            새 소장
          </Link>
        </div>
      </div>

      {/* 탭 */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => {
                setActiveTab('active');
                setPage(1);
              }}
              className={`py-2 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'active'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              활성화
            </button>
            <button
              onClick={() => {
                setActiveTab('inactive');
                setPage(1);
              }}
              className={`py-2 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'inactive'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              비활성화
            </button>
            <button
              onClick={() => {
                setActiveTab('ios');
                setPage(1);
              }}
              className={`py-2 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'ios'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="inline-flex items-center gap-1">
                🍎 아이폰
              </span>
            </button>
            <button
              onClick={() => {
                setActiveTab('all');
                setPage(1);
              }}
              className={`py-2 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'all'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              전체
            </button>
          </nav>
        </div>

        {/* 검색 및 페이지 크기 선택 */}
        <div className="p-4 lg:p-6">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="flex-1 max-w-lg">
              <input
                type="text"
                placeholder="소장명, 회사명, 전화번호로 검색..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1); // 검색 시 페이지 리셋
                }}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">페이지당:</span>
              <select
                value={pageSize}
                onChange={(e) => { 
                  setPageSize(parseInt(e.target.value)); 
                  setPage(1); 
                }}
                className="px-2 py-1 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
              >
                <option value="25">25개</option>
                <option value="50">50개</option>
                <option value="100">100개</option>
                <option value="200">200개</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">로딩 중...</p>
          </div>
        ) : (
          <>
            {/* 모바일/태블릿: 카드 레이아웃 */}
            <div className="xl:hidden space-y-3 p-4">
              {contractors?.map((contractor) => (
                <div key={contractor.id} className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 truncate flex items-center gap-1" title={contractor.name}>
                        {contractor.name}
                        {contractor.is_ios_user && <span title="iOS 사용자">🍎</span>}
                      </h3>
                      <p className="text-xs text-gray-500 truncate" title={contractor.company_name}>
                        {contractor.company_name}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        ID: {contractor.login_id || contractor.email}
                      </p>
                    </div>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
                      contractor.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {contractor.is_active ? '활성' : '비활성'}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">연락처:</span>
                      <span className="text-gray-900">{contractor.phone}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">평점:</span>
                      <div className="flex items-center">
                        <span className="text-gray-900">{contractor.rating?.toFixed(1) || '0.0'}</span>
                        <span className="ml-1 text-yellow-400">★</span>
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500">전문 분야:</span>
                      <div className="mt-1">
                        {contractor.specialties?.split(',').map(spec => (
                          <span key={spec} className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded-full mr-1 mb-1">
                            {spec.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                    {contractor.admin_notes && (
                      <div className="pt-2 mt-2 border-t border-gray-100">
                        <span className="text-gray-500 text-xs">관리자 메모:</span>
                        <p className="text-xs text-gray-700 mt-1 italic">{contractor.admin_notes}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2 mt-3 pt-3 border-t">
                    <Link
                      to={`/admin/contractors/${contractor.id}/edit`}
                      className="flex-1 text-center px-2 py-1.5 bg-indigo-50 text-indigo-700 text-xs font-medium rounded hover:bg-indigo-100"
                    >
                      수정
                    </Link>
                    <button
                      onClick={() => setCommissionModalContractor(contractor)}
                      className="flex-1 text-center px-2 py-1.5 bg-green-50 text-green-700 text-xs font-medium rounded hover:bg-green-100"
                    >
                      수수료
                    </button>
                    <button
                      onClick={() => handleToggleIosUser(contractor)}
                      className={`flex-1 text-center px-2 py-1.5 text-xs font-medium rounded ${
                        contractor.is_ios_user 
                          ? 'bg-orange-50 text-orange-700 hover:bg-orange-100' 
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                      title={contractor.is_ios_user ? 'iOS 사용자 해제' : 'iOS 사용자 설정'}
                    >
                      {contractor.is_ios_user ? '🍎 해제' : '🍎 설정'}
                    </button>
                    <button
                      onClick={() => handleToggleActive(contractor.id, contractor.is_active)}
                      className={`flex-1 text-center px-2 py-1.5 text-xs font-medium rounded ${
                        contractor.is_active 
                          ? 'bg-red-50 text-red-700 hover:bg-red-100' 
                          : 'bg-green-50 text-green-700 hover:bg-green-100'
                      }`}
                    >
                      {contractor.is_active ? '비활성' : '활성'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* PC: 테이블 레이아웃 */}
            <div className="hidden xl:block">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    소장 정보
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    연락처
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    전문 분야
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    평점
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    액션
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {contractors?.map((contractor) => (
                  <tr key={contractor.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="max-w-xs">
                        <div className="text-sm font-medium text-gray-900 flex items-center gap-1">
                          {contractor.name}
                          {contractor.is_ios_user && <span title="iOS 사용자">🍎</span>}
                        </div>
                        <div className="text-sm text-gray-500 truncate" title={contractor.company_name}>
                          {contractor.company_name}
                        </div>
                        <div className="text-sm text-gray-400">
                          ID: {contractor.login_id || contractor.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {contractor.phone}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {contractor.specialties?.split(',').map(spec => (
                          <span key={spec} className="inline-block bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full mr-1 mb-1">
                            {spec.trim()}
                          </span>
                        ))}
                        {contractor.admin_notes && (
                          <div className="mt-2 pt-2 border-t border-gray-100">
                            <span className="text-xs text-gray-500">메모: </span>
                            <span className="text-xs text-gray-700 italic">{contractor.admin_notes}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-sm text-gray-900">
                          {contractor.rating?.toFixed(1) || '0.0'}
                        </span>
                        <span className="ml-1 text-yellow-400">★</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        contractor.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {contractor.is_active ? '활성' : '비활성'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center justify-center gap-2">
                        <Link
                          to={`/admin/contractors/${contractor.id}/edit`}
                          className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 transition-colors inline-block"
                        >
                          수정
                        </Link>
                        <button
                          onClick={() => setCommissionModalContractor(contractor)}
                          className="px-3 py-1 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                        >
                          수수료
                        </button>
                        <button
                          onClick={() => handleToggleIosUser(contractor)}
                          className={`px-2 py-1 rounded-md transition-colors ${
                            contractor.is_ios_user 
                              ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' 
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          title={contractor.is_ios_user ? 'iOS 사용자 해제' : 'iOS 사용자 설정'}
                        >
                          {contractor.is_ios_user ? '🍎' : '📱'}
                        </button>
                        <button
                          onClick={() => handleToggleActive(contractor.id, contractor.is_active)}
                          className={`px-3 py-1 rounded-md transition-colors ${
                            contractor.is_active 
                              ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          {contractor.is_active ? '비활성화' : '활성화'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* 페이지네이션 */}
      {contractorsData?.pages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              이전
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === contractorsData.pages}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              다음
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                총 <span className="font-medium">{contractorsData.total || 0}</span>명
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300">
                  {page} / {contractorsData.pages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === contractorsData.pages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
      
      {commissionModalContractor && (
        <ContractorCommissionModal
          isOpen={!!commissionModalContractor}
          onClose={() => setCommissionModalContractor(null)}
          contractor={commissionModalContractor}
          onSave={() => refetch()}
        />
      )}
    </div>
  );
};

const CustomerManagement = () => {
  const queryClient = useQueryClient();
  const [filter, setFilter] = React.useState('all');
  const [activeTab, setActiveTab] = React.useState('active'); // 'active', 'inactive', 'all'
  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState('');
  const [sortBy, setSortBy] = React.useState('created_at');
  const [sortOrder, setSortOrder] = React.useState('desc');
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25); // 기본값 25
  const [showVipModal, setShowVipModal] = React.useState(false);
  const [showEditModal, setShowEditModal] = React.useState(false);
  const [selectedCustomer, setSelectedCustomer] = React.useState(null);
  const [editForm, setEditForm] = React.useState({
    name: '',
    login_id: '',
    email: '',
    password: ''
  });
  const [idCheckMessage, setIdCheckMessage] = React.useState('');

  const { data: customersData, isLoading, refetch } = useQuery(
    ['admin-customers', filter, activeTab, dateFrom, dateTo, sortBy, sortOrder, search, page, pageSize],
    () => {
      const params = { 
        user_type: filter !== 'all' ? filter : undefined, 
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        search,
        page,
        limit: pageSize
      };
      
      // activeTab에 따라 필터 파라미터 설정
      if (activeTab === 'active') {
        params.is_active = 'true';
      } else if (activeTab === 'inactive') {
        params.is_active = 'false';
      } else if (activeTab === 'ios') {
        params.is_ios = 'true';
      }
      // 'all'인 경우 파라미터를 포함하지 않음
      
      return api.get('/admin/customers', { params }).then(res => res.data);
    },
    { keepPreviousData: true }
  );
  
  const customers = customersData?.customers || [];

  const handleConvertToVip = async (data) => {
    try {
      await api.post(`/admin/customers/${data.customerId}/convert-to-vip`, {
        reward_rate: data.rewardRate
      });
      toast.success('VIP 고객으로 전환되었습니다.');
      refetch();
      queryClient.invalidateQueries('admin-vip'); // VIP 목록 새로고침
      queryClient.invalidateQueries('admin-vip-customers'); // VIP 고객 목록 새로고침
      setShowVipModal(false);
    } catch (error) {
      console.error('VIP 전환 실패:', error);
      toast.error(error.response?.data?.error || 'VIP 전환에 실패했습니다.');
    }
  };

  const handleToggleActive = async (customerId, isActive) => {
    try {
      await api.patch(`/admin/customers/${customerId}/toggle-active`, { is_active: !isActive });
      refetch();
    } catch (error) {
      console.error('고객 활성화 상태 변경 실패:', error);
    }
  };

  // VIP 고객 정보 수정 관련 함수들
  const handleEditCustomer = async (customer) => {
    setSelectedCustomer(customer);
    
    // 고객 상세 정보 가져오기 (비밀번호 포함)
    try {
      const response = await api.get(`/admin/customers/${customer.id}/info`);
      const customerInfo = response.data.user;
      
      setEditForm({
        name: customerInfo.name || '',
        login_id: customerInfo.login_id || '',
        email: customerInfo.email || '',
        password: customerInfo.password || ''  // 현재 비밀번호 표시
      });
    } catch (error) {
      console.error('고객 정보 조회 실패:', error);
      // 실패 시 기본 정보만 사용
      setEditForm({
        name: customer.name || '',
        login_id: customer.login_id || '',
        email: customer.email || '',
        password: ''
      });
    }
    
    setIdCheckMessage('');
    setShowEditModal(true);
  };

  const checkLoginId = async (loginId) => {
    if (!loginId) {
      setIdCheckMessage('');
      return;
    }
    
    try {
      const response = await api.get('/admin/customers/check-login-id', {
        params: { 
          login_id: loginId,
          exclude_id: selectedCustomer?.id
        }
      });
      setIdCheckMessage(response.data.message);
    } catch (error) {
      console.error('ID 중복 체크 실패:', error);
      setIdCheckMessage('중복 체크 실패');
    }
  };

  const handleUpdateCustomer = async () => {
    try {
      // 빈 문자열이면 null로 변환
      const updateData = {
        name: editForm.name,
        login_id: editForm.login_id || null,
        email: editForm.email || null
      };
      
      // 비밀번호가 입력된 경우에만 포함
      if (editForm.password) {
        updateData.password = editForm.password;
      }

      await api.put(`/admin/customers/${selectedCustomer.id}`, updateData);
      toast.success('고객 정보가 수정되었습니다.');
      setShowEditModal(false);
      refetch();
    } catch (error) {
      console.error('고객 정보 수정 실패:', error);
      toast.error(error.response?.data?.error || '고객 정보 수정에 실패했습니다.');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl lg:text-2xl font-semibold text-gray-900">고객 관리</h1>
      </div>

      {/* 탭 */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => {
                setActiveTab('active');
                setPage(1);
              }}
              className={`py-2 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'active'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              활성화
            </button>
            <button
              onClick={() => {
                setActiveTab('inactive');
                setPage(1);
              }}
              className={`py-2 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'inactive'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              비활성화
            </button>
            <button
              onClick={() => {
                setActiveTab('all');
                setPage(1);
              }}
              className={`py-2 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'all'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              전체
            </button>
          </nav>
        </div>
      </div>

      {/* 필터 및 검색 */}
      <div className="bg-white shadow rounded-lg p-4 lg:p-6 mb-6">
        <div className="space-y-4">
          {/* 첫번째 줄: 고객 타입 및 정렬 */}
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={filter}
              onChange={(e) => { setFilter(e.target.value); setPage(1); }}
              className="block w-full sm:w-auto px-3 py-2 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
            >
              <option value="all">전체 고객</option>
              <option value="customer">일반 고객</option>
              <option value="vip_customer">VIP 고객</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="block w-full sm:w-auto px-3 py-2 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
            >
              <option value="created_at">가입일순</option>
              <option value="name">이름순</option>
              <option value="project_count">프로젝트수순</option>
              <option value="total_amount">총계약금액순</option>
              <option value="last_project">최근프로젝트순</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                sortOrder === 'desc' 
                  ? 'bg-gray-600 text-white hover:bg-gray-700' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {sortOrder === 'desc' ? '↓ 내림차순' : '↑ 오름차순'}
            </button>
          </div>
          
          {/* 두번째 줄: 날짜 필터 및 검색 */}
          <div className="flex flex-col lg:flex-row gap-2">
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="block w-full sm:w-auto px-3 py-2 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
                placeholder="시작일"
              />
              <span className="self-center text-gray-500 hidden sm:inline">~</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="block w-full sm:w-auto px-3 py-2 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
                placeholder="종료일"
              />
              {(dateFrom || dateTo) && (
                <button
                  onClick={() => { setDateFrom(''); setDateTo(''); }}
                  className="px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  날짜 초기화
                </button>
              )}
            </div>
            <input
              type="text"
              placeholder="고객명, 연락처, 이메일로 검색..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block w-full lg:flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          </div>
          
          {/* 세번째 줄: 요약 정보 및 엑셀 다운로드 */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                총 <span className="font-semibold">{customersData?.total || 0}</span>명 고객
                {filter !== 'all' && (
                  <span className="ml-2">
                    ({filter === 'vip_customer' ? 'VIP' : '일반'} 고객만)
                  </span>
                )}
                {(dateFrom || dateTo) && (
                  <span className="ml-2">
                    ({dateFrom || '처음'}부터 {dateTo || '현재'}까지)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">페이지당:</span>
                <select
                  value={pageSize}
                  onChange={(e) => { 
                    setPageSize(parseInt(e.target.value)); 
                    setPage(1); 
                  }}
                  className="px-2 py-1 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
                >
                  <option value="25">25명</option>
                  <option value="50">50명</option>
                  <option value="100">100명</option>
                  <option value="200">200명</option>
                </select>
              </div>
            </div>
            <button
              onClick={async () => {
                try {
                  const response = await api.get('/admin/customers/export', {
                    params: {
                      user_type: filter !== 'all' ? filter : undefined,
                      date_from: dateFrom || undefined,
                      date_to: dateTo || undefined,
                      search
                    },
                    responseType: 'blob'
                  });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `customers_${new Date().toISOString().split('T')[0]}.xlsx`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  toast.success('엑셀 파일이 다운로드되었습니다.');
                } catch (error) {
                  toast.error('엑셀 다운로드에 실패했습니다.');
                }
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
            >
              📊 엑셀 다운로드
            </button>
          </div>
        </div>
      </div>

      {/* 고객 목록 */}
      <div className="bg-white shadow rounded-lg">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">로딩 중...</p>
          </div>
        ) : (
          <>
            {/* 모바일/태블릿: 카드 레이아웃 */}
            <div className="xl:hidden space-y-3 p-4">
              {customers?.map((customer) => (
                <div key={customer.id} className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-900">
                          {customer.name}
                          {customer.user_type === 'vip_customer' && customer.login_id && (
                            <span className="ml-2 text-xs text-blue-600 font-normal">({customer.login_id})</span>
                          )}
                        </h3>
                        {!!customer.vip_recommended && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                            VIP 추천
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        가입일: {new Date(customer.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
                      customer.user_type === 'vip_customer' 
                        ? 'bg-yellow-100 text-yellow-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {customer.user_type === 'vip_customer' ? 'VIP' : '일반'}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">연락처:</span>
                      <span className="text-gray-900">{customer.phone}</span>
                    </div>
                    {customer.user_type === 'vip_customer' && customer.email && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">이메일:</span>
                        <span className="text-gray-900 truncate max-w-[150px]" title={customer.email}>
                          {customer.email}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">프로젝트:</span>
                      <span className="text-gray-900">
                        전체 {customer.project_count || 0}건
                        {customer.completed_count > 0 && <span className="text-green-600 ml-1">(완료 {customer.completed_count})</span>}
                        {customer.ongoing_count > 0 && <span className="text-blue-600 ml-1">(진행 {customer.ongoing_count})</span>}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">누적 금액:</span>
                      <span className="text-gray-900 font-semibold">
                        {customer.total_amount ? `${(customer.total_amount / 10000).toLocaleString()}만원` : '-'}
                      </span>
                    </div>
                    {customer.last_project_date && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">최근 프로젝트:</span>
                        <span className="text-gray-900">
                          {new Date(customer.last_project_date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">상태:</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        customer.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {customer.is_active ? '활성' : '비활성'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-3 pt-3 border-t">
                    {customer.user_type === 'customer' && (
                      <button
                        onClick={() => {
                          setSelectedCustomer(customer);
                          setShowVipModal(true);
                        }}
                        className={`flex-1 text-center px-2 py-1.5 text-xs font-medium rounded ${
                          customer.vip_recommended 
                            ? 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100' 
                            : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                        }`}
                      >
                        VIP 전환
                      </button>
                    )}
                    <button
                      onClick={() => handleEditCustomer(customer)}
                      className="flex-1 text-center px-2 py-1.5 text-xs font-medium rounded bg-blue-50 text-blue-700 hover:bg-blue-100"
                    >
                      정보 수정
                    </button>
                    <button
                      onClick={() => handleToggleActive(customer.id, customer.is_active)}
                      className={`flex-1 text-center px-2 py-1.5 text-xs font-medium rounded ${
                        customer.is_active 
                          ? 'bg-red-50 text-red-700 hover:bg-red-100' 
                          : 'bg-green-50 text-green-700 hover:bg-green-100'
                      }`}
                    >
                      {customer.is_active ? '비활성화' : '활성화'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* PC: 테이블 레이아웃 */}
            <div className="hidden xl:block">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    고객 정보
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    연락처
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    타입
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    프로젝트
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    누적 금액
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    액션
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {customers?.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {customer.name}
                          {customer.user_type === 'vip_customer' && customer.login_id && (
                            <span className="ml-2 text-xs text-blue-600 font-normal">({customer.login_id})</span>
                          )}
                          {!!customer.vip_recommended && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                              VIP 추천
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          가입일: {new Date(customer.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {customer.phone}
                      </div>
                      <div className="text-sm text-gray-500">
                        {customer.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        customer.user_type === 'vip_customer' 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {customer.user_type === 'vip_customer' ? 'VIP' : '일반'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        전체: {customer.project_count || 0}건
                      </div>
                      <div className="text-xs text-gray-500">
                        {customer.completed_count > 0 && <span className="text-green-600">완료 {customer.completed_count}</span>}
                        {customer.completed_count > 0 && customer.ongoing_count > 0 && <span className="mx-1">·</span>}
                        {customer.ongoing_count > 0 && <span className="text-blue-600">진행 {customer.ongoing_count}</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-semibold">
                        {customer.total_amount ? `${(customer.total_amount / 10000).toLocaleString()}만원` : '-'}
                      </div>
                      {customer.last_project_date && (
                        <div className="text-xs text-gray-500">
                          최근: {new Date(customer.last_project_date).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        customer.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {customer.is_active ? '활성' : '비활성'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {customer.user_type === 'customer' && (
                        <button
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setShowVipModal(true);
                          }}
                          className={`${
                            customer.vip_recommended 
                              ? 'text-yellow-600 hover:text-yellow-900 font-semibold' 
                              : 'text-indigo-600 hover:text-indigo-900'
                          } mr-4`}
                        >
                          VIP 전환
                        </button>
                      )}
                      <button
                        onClick={() => handleEditCustomer(customer)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        정보 수정
                      </button>
                      <button
                        onClick={() => handleToggleActive(customer.id, customer.is_active)}
                        className={`${customer.is_active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`}
                      >
                        {customer.is_active ? '비활성화' : '활성화'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* 페이지네이션 */}
      {customersData?.pages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              이전
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === customersData.pages}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              다음
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                총 <span className="font-medium">{customersData.total}</span>명 중{' '}
                <span className="font-medium">{(page - 1) * pageSize + 1}</span>-
                <span className="font-medium">{Math.min(page * pageSize, customersData.total)}</span>명 표시
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                
                {/* 페이지 번호 표시 로직 개선 */}
                {(() => {
                  const totalPages = customersData.pages;
                  const maxButtons = 7; // 표시할 최대 버튼 수
                  let startPage = Math.max(1, page - Math.floor(maxButtons / 2));
                  let endPage = Math.min(totalPages, startPage + maxButtons - 1);
                  
                  if (endPage - startPage < maxButtons - 1) {
                    startPage = Math.max(1, endPage - maxButtons + 1);
                  }
                  
                  const pageNumbers = [];
                  
                  // 첫 페이지
                  if (startPage > 1) {
                    pageNumbers.push(
                      <button
                        key={1}
                        onClick={() => setPage(1)}
                        className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                      >
                        1
                      </button>
                    );
                    
                    if (startPage > 2) {
                      pageNumbers.push(
                        <span key="dots1" className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 bg-white">
                          ...
                        </span>
                      );
                    }
                  }
                  
                  // 중간 페이지들
                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <button
                        key={i}
                        onClick={() => setPage(i)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                          page === i
                            ? 'z-10 bg-indigo-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                            : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }
                  
                  // 마지막 페이지
                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pageNumbers.push(
                        <span key="dots2" className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 bg-white">
                          ...
                        </span>
                      );
                    }
                    
                    pageNumbers.push(
                      <button
                        key={totalPages}
                        onClick={() => setPage(totalPages)}
                        className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                      >
                        {totalPages}
                      </button>
                    );
                  }
                  
                  return pageNumbers;
                })()}
                
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === customersData.pages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* VIP 전환 모달 */}
      {showVipModal && selectedCustomer && (
        <VipConversionModal
          customer={selectedCustomer}
          onClose={() => {
            setShowVipModal(false);
            setSelectedCustomer(null);
          }}
          onConfirm={handleConvertToVip}
        />
      )}
      
      {/* VIP 정보 수정 모달 */}
      {showEditModal && selectedCustomer && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full m-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              고객 정보 수정
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">이름</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">로그인 ID</label>
                <input
                  type="text"
                  value={editForm.login_id}
                  onChange={(e) => {
                    setEditForm({...editForm, login_id: e.target.value});
                    if (e.target.value) checkLoginId(e.target.value);
                  }}
                  placeholder="로그인 ID (선택)"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                {idCheckMessage && (
                  <p className={`mt-1 text-sm ${
                    idCheckMessage.includes('사용 가능') ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {idCheckMessage}
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">이메일</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                  placeholder="이메일 (선택)"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">비밀번호</label>
                <input
                  type="text"
                  value={editForm.password}
                  onChange={(e) => setEditForm({...editForm, password: e.target.value})}
                  placeholder="새 비밀번호 (변경하지 않으면 비워두세요)"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  비밀번호 변경 시 모든 프로젝트의 접속 비밀번호도 함께 변경됩니다
                </p>
              </div>
              
              <div className="bg-yellow-50 p-3 rounded-md">
                <p className="text-sm text-yellow-800">
                  <strong>연락처:</strong> {selectedCustomer.phone} (변경 불가)
                </p>
                <p className="text-xs text-yellow-600 mt-1">
                  연락처는 프로젝트와 연결되어 있어 변경할 수 없습니다
                </p>
              </div>
            </div>
            
            <div className="mt-6 flex gap-3">
              <button
                onClick={handleUpdateCustomer}
                disabled={idCheckMessage.includes('사용중')}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                수정
              </button>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedCustomer(null);
                  setEditForm({name: '', login_id: '', email: '', password: ''});
                  setIdCheckMessage('');
                }}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const VipManagement = () => {
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25); // 기본값 25
  
  const { data: vipDataResponse, isLoading, refetch } = useQuery(
    ['admin-vip-customers', page, pageSize],
    () => api.get('/admin/vip/customers', {
      params: { page, limit: pageSize }
    }).then(res => res.data),
    { keepPreviousData: true }
  );
  
  const vipData = vipDataResponse?.vipCustomers || [];

  const { data: withdrawalData } = useQuery(
    'admin-vip-pending-withdrawals',
    () => api.get('/admin/vip/withdrawals?status=pending').then(res => res.data)
  );

  // 전체 통계 계산
  const totalStats = React.useMemo(() => {
    if (!vipData) return null;
    
    const totalBalance = vipData.reduce((sum, vip) => sum + (vip.current_balance || 0), 0);
    const totalEarned = vipData.reduce((sum, vip) => sum + (vip.total_earned || 0), 0);
    const totalWithdrawn = vipData.reduce((sum, vip) => sum + (vip.total_withdrawn || 0), 0);
    
    // 대기 중인 인출 요청 계산
    let pendingWithdrawals = 0;
    let pendingNetAmount = 0;
    
    if (withdrawalData && Array.isArray(withdrawalData)) {
      withdrawalData.forEach(w => {
        if (w.status === 'pending') {
          pendingWithdrawals += w.amount || 0;
          // 수수료 계산
          const feeAmount = w.fee_amount || 0;
          const netAmount = (w.amount || 0) - feeAmount;
          pendingNetAmount += netAmount;
        }
      });
    }
    
    return {
      totalBalance,
      totalEarned,
      totalWithdrawn,
      pendingWithdrawals,
      pendingNetAmount,
      vipCount: vipData.length
    };
  }, [vipData, withdrawalData]);

  return (
    <div>
      <h1 className="text-xl lg:text-2xl font-semibold text-gray-900 mb-6">VIP 고객 관리</h1>

      {/* 전체 통계 카드 */}
      {totalStats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">VIP 고객 수</p>
                <p className="text-2xl font-bold text-gray-900">{totalStats.vipCount}명</p>
              </div>
              <UserGroupIcon className="h-10 w-10 text-indigo-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">전체 잔액</p>
                <p className="text-2xl font-bold text-gray-900">{totalStats.totalBalance.toLocaleString()}원</p>
              </div>
              <CurrencyDollarIcon className="h-10 w-10 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">대기중 인출 요청</p>
                <p className="text-2xl font-bold text-orange-600">{totalStats.pendingWithdrawals.toLocaleString()}원</p>
                <p className="text-xs text-gray-600 mt-1">수수료 포함</p>
              </div>
              <ClockIcon className="h-10 w-10 text-orange-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">실제 지급액</p>
                <p className="text-2xl font-bold text-red-600">{totalStats.pendingNetAmount.toLocaleString()}원</p>
                <p className="text-xs text-gray-600 mt-1">수수료 제외</p>
              </div>
              <BanknotesIcon className="h-10 w-10 text-red-500" />
            </div>
          </div>
        </div>
      )}

      {/* 페이지 크기 선택 */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            총 <span className="font-semibold">{vipDataResponse?.total || 0}</span>명의 VIP 고객
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">페이지당:</span>
            <select
              value={pageSize}
              onChange={(e) => { 
                setPageSize(parseInt(e.target.value)); 
                setPage(1); 
              }}
              className="px-2 py-1 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
            >
              <option value="25">25명</option>
              <option value="50">50명</option>
              <option value="100">100명</option>
              <option value="200">200명</option>
            </select>
          </div>
        </div>
      </div>

      {/* VIP 수수료 설정 */}
      <VipFeeSettings />

      {/* VIP 고객 목록 */}
        <div className="bg-white shadow rounded-lg">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">로딩 중...</p>
            </div>
          ) : (
            <>
              {/* 모바일/태블릿 카드 레이아웃 */}
              <div className="xl:hidden p-4 space-y-4">
                {vipData?.map((vip) => (
                  <div key={vip.id} className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">{vip.User?.name}</h3>
                        <p className="text-xs text-gray-500 mt-1">{vip.User?.phone}</p>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                        {vip.reward_rate}%
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <p className="text-xs text-gray-500">적립 총액</p>
                        <p className="text-sm font-semibold text-gray-900 mt-1">
                          {vip.total_earned?.toLocaleString()}원
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">인출 총액</p>
                        <p className="text-sm font-semibold text-gray-900 mt-1">
                          {vip.total_withdrawn?.toLocaleString()}원
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">현재 잔액</p>
                        <p className="text-sm font-semibold text-indigo-600 mt-1">
                          {vip.current_balance?.toLocaleString()}원
                        </p>
                      </div>
                    </div>
                    
                    <div className="pt-3 border-t border-gray-200">
                      <Link
                        to={`/admin/vip/${vip.id}/details`}
                        className="block w-full text-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 transition-colors"
                      >
                        상세보기
                      </Link>
                    </div>
                  </div>
                ))}
              </div>

              {/* PC 테이블 레이아웃 */}
              <div className="hidden xl:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        VIP 고객
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        리워드 비율
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        적립 총액
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        인출 총액
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        현재 잔액
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        액션
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {vipData?.map((vip) => (
                      <tr key={vip.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {vip.User?.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {vip.User?.phone}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {vip.reward_rate}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {vip.total_earned?.toLocaleString()}원
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {vip.total_withdrawn?.toLocaleString()}원
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {vip.current_balance?.toLocaleString()}원
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <Link
                            to={`/admin/vip/${vip.id}/details`}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            상세보기
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        
        {/* 페이지네이션 */}
        {vipDataResponse?.pages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
            <div className="flex flex-1 justify-between sm:hidden">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                이전
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === vipDataResponse.pages}
                className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                다음
              </button>
            </div>
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  총 <span className="font-medium">{vipDataResponse.total}</span>명 중{' '}
                  <span className="font-medium">{(page - 1) * pageSize + 1}</span>-
                  <span className="font-medium">{Math.min(page * pageSize, vipDataResponse.total)}</span>명 표시
                </p>
              </div>
              <div>
                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  
                  {/* 페이지 번호 표시 로직 개선 */}
                  {(() => {
                    const totalPages = vipDataResponse.pages;
                    const maxButtons = 7;
                    let startPage = Math.max(1, page - Math.floor(maxButtons / 2));
                    let endPage = Math.min(totalPages, startPage + maxButtons - 1);
                    
                    if (endPage - startPage < maxButtons - 1) {
                      startPage = Math.max(1, endPage - maxButtons + 1);
                    }
                    
                    const pageNumbers = [];
                    
                    if (startPage > 1) {
                      pageNumbers.push(
                        <button
                          key={1}
                          onClick={() => setPage(1)}
                          className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                        >
                          1
                        </button>
                      );
                      
                      if (startPage > 2) {
                        pageNumbers.push(
                          <span key="dots1" className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 bg-white">
                            ...
                          </span>
                        );
                      }
                    }
                    
                    for (let i = startPage; i <= endPage; i++) {
                      pageNumbers.push(
                        <button
                          key={i}
                          onClick={() => setPage(i)}
                          className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                            page === i
                              ? 'z-10 bg-indigo-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                              : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    }
                    
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pageNumbers.push(
                          <span key="dots2" className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 bg-white">
                            ...
                          </span>
                        );
                      }
                      
                      pageNumbers.push(
                        <button
                          key={totalPages}
                          onClick={() => setPage(totalPages)}
                          className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                        >
                          {totalPages}
                        </button>
                      );
                    }
                    
                    return pageNumbers;
                  })()}
                  
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === vipDataResponse.pages}
                    className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

const VipWithdrawals = () => {
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25);
  
  const { data: vipDataResponse, isLoading, refetch } = useQuery(
    ['admin-vip-withdrawals', page, pageSize],
    () => api.get('/admin/vip/withdrawals', {
      params: { page, limit: pageSize }
    }).then(res => res.data),
    { keepPreviousData: true }
  );
  
  const vipData = vipDataResponse?.withdrawals || vipDataResponse || [];

  const [selectedWithdrawal, setSelectedWithdrawal] = React.useState(null);
  const [showMemoModal, setShowMemoModal] = React.useState(false);
  const [memoForm, setMemoForm] = React.useState({ memo: '', adminMemo: '' });

  // 상태별 통계 계산
  const withdrawalStats = React.useMemo(() => {
    if (!vipData || !Array.isArray(vipData)) return null;
    
    let pending = { count: 0, amount: 0, netAmount: 0 };
    let approved = { count: 0, amount: 0, netAmount: 0 };
    let completed = { count: 0, amount: 0, netAmount: 0 };
    let rejected = { count: 0, amount: 0 };
    
    vipData.forEach(w => {
      const amount = w.amount || 0;
      const feeAmount = w.fee_amount || 0;
      const netAmount = amount - feeAmount;
      
      switch (w.status) {
        case 'pending':
          pending.count++;
          pending.amount += amount;
          pending.netAmount += netAmount;
          break;
        case 'approved':
          approved.count++;
          approved.amount += amount;
          approved.netAmount += netAmount;
          break;
        case 'completed':
          completed.count++;
          completed.amount += amount;
          completed.netAmount += netAmount;
          break;
        case 'rejected':
          rejected.count++;
          rejected.amount += amount;
          break;
      }
    });
    
    return { pending, approved, completed, rejected };
  }, [vipData]);

  const handleWithdrawalAction = async (withdrawalId, action, reason = '', memo = '', adminMemo = '') => {
    try {
      await api.patch(`/admin/vip/withdrawals/${withdrawalId}`, {
        action,
        reason,
        memo,
        adminMemo
      });
      // 데이터 새로고침
      refetch();
      setShowMemoModal(false);
      setSelectedWithdrawal(null);
      setMemoForm({ memo: '', adminMemo: '' });
    } catch (error) {
      console.error('인출 요청 처리 실패:', error);
    }
  };

  const handleUpdateMemo = async (withdrawalId) => {
    try {
      await api.put(`/admin/vip/withdrawals/${withdrawalId}/memo`, memoForm);
      refetch();
      setShowMemoModal(false);
      setSelectedWithdrawal(null);
      setMemoForm({ memo: '', adminMemo: '' });
      alert('메모가 수정되었습니다.');
    } catch (error) {
      console.error('메모 수정 실패:', error);
      alert('메모 수정에 실패했습니다.');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl lg:text-2xl font-semibold text-gray-900">인출 요청 관리</h1>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">표시 개수:</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value={25}>25개</option>
            <option value={50}>50개</option>
            <option value={100}>100개</option>
            <option value={200}>200개</option>
          </select>
        </div>
      </div>

      {/* 상태별 통계 카드 */}
      {withdrawalStats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">대기중</p>
                <p className="text-xl font-bold text-gray-900">{withdrawalStats.pending.count}건</p>
                <p className="text-sm text-gray-600 mt-1">{withdrawalStats.pending.amount.toLocaleString()}원</p>
                <p className="text-xs text-red-600 font-semibold">실지급: {withdrawalStats.pending.netAmount.toLocaleString()}원</p>
              </div>
              <ClockIcon className="h-8 w-8 text-yellow-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">승인됨</p>
                <p className="text-xl font-bold text-gray-900">{withdrawalStats.approved.count}건</p>
                <p className="text-sm text-gray-600 mt-1">{withdrawalStats.approved.amount.toLocaleString()}원</p>
                <p className="text-xs text-red-600 font-semibold">실지급: {withdrawalStats.approved.netAmount.toLocaleString()}원</p>
              </div>
              <CheckCircleIcon className="h-8 w-8 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">완료</p>
                <p className="text-xl font-bold text-gray-900">{withdrawalStats.completed.count}건</p>
                <p className="text-sm text-gray-600 mt-1">{withdrawalStats.completed.amount.toLocaleString()}원</p>
                <p className="text-xs text-green-600 font-semibold">지급완료: {withdrawalStats.completed.netAmount.toLocaleString()}원</p>
              </div>
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">거절</p>
                <p className="text-xl font-bold text-gray-900">{withdrawalStats.rejected.count}건</p>
                <p className="text-sm text-gray-600 mt-1">{withdrawalStats.rejected.amount.toLocaleString()}원</p>
              </div>
              <XCircleIcon className="h-8 w-8 text-red-500" />
            </div>
          </div>
        </div>
      )}

      {/* VIP 수수료 설정 */}
      <VipFeeSettings />

      {/* 인출 요청 목록 */}
        <div className="bg-white shadow rounded-lg">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">로딩 중...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      요청자
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      인출 금액
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      수수료/실지급액
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      입금 계좌
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      메모
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      상태
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      요청일
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      액션
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {vipData?.map((withdrawal) => (
                    <tr key={withdrawal.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {withdrawal.customer_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {withdrawal.customer_phone}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {withdrawal.amount?.toLocaleString()}원
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          수수료: {withdrawal.fee_rate || 0}% ({(withdrawal.fee_amount || 0).toLocaleString()}원)
                        </div>
                        <div className="text-sm font-semibold text-blue-600">
                          실지급: {(withdrawal.net_amount || withdrawal.amount).toLocaleString()}원
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {withdrawal.bank_name} {withdrawal.bank_account}
                        </div>
                        <div className="text-sm text-gray-500">
                          {withdrawal.account_holder}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          {withdrawal.memo && (
                            <div className="text-gray-900 mb-1">
                              <span className="font-medium">고객용:</span> {withdrawal.memo}
                            </div>
                          )}
                          {withdrawal.admin_memo && (
                            <div className="text-gray-600">
                              <span className="font-medium">관리자용:</span> {withdrawal.admin_memo}
                            </div>
                          )}
                          {(withdrawal.status === 'pending' || withdrawal.status === 'approved' || withdrawal.status === 'completed') && (
                            <button
                              onClick={() => {
                                setSelectedWithdrawal(withdrawal);
                                setMemoForm({ 
                                  memo: withdrawal.memo || '', 
                                  adminMemo: withdrawal.admin_memo || '' 
                                });
                                setShowMemoModal(true);
                              }}
                              className="text-xs text-indigo-600 hover:text-indigo-900 mt-1"
                            >
                              메모 수정
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          withdrawal.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          withdrawal.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                          withdrawal.status === 'completed' ? 'bg-green-100 text-green-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {withdrawal.status === 'pending' ? '대기' :
                           withdrawal.status === 'approved' ? '승인' :
                           withdrawal.status === 'completed' ? '완료' : '거절'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(withdrawal.requested_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {withdrawal.status === 'pending' && (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                setSelectedWithdrawal(withdrawal);
                                setMemoForm({ memo: withdrawal.memo || '', adminMemo: withdrawal.admin_memo || '' });
                                setShowMemoModal(true);
                              }}
                              className="text-green-600 hover:text-green-900"
                            >
                              승인
                            </button>
                            <button
                              onClick={() => handleWithdrawalAction(withdrawal.id, 'reject', '관리자 거절')}
                              className="text-red-600 hover:text-red-900"
                            >
                              거절
                            </button>
                          </div>
                        )}
                        {withdrawal.status === 'approved' && (
                          <button
                            onClick={() => handleWithdrawalAction(withdrawal.id, 'complete')}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            완료 처리
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      {/* 페이지네이션 */}
      {vipDataResponse && vipDataResponse.pages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              이전
            </button>
            <button
              onClick={() => setPage(Math.min(vipDataResponse.pages, page + 1))}
              disabled={page === vipDataResponse.pages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              다음
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                전체 <span className="font-medium">{vipDataResponse.total}</span>개 중{' '}
                <span className="font-medium">{((page - 1) * pageSize) + 1}</span> -{' '}
                <span className="font-medium">{Math.min(page * pageSize, vipDataResponse.total)}</span> 표시
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">이전</span>
                  ◀
                </button>
                
                {(() => {
                  const totalPages = vipDataResponse.pages;
                  const maxButtons = 7;
                  let startPage = Math.max(1, page - Math.floor(maxButtons / 2));
                  let endPage = Math.min(totalPages, startPage + maxButtons - 1);
                  
                  if (endPage - startPage < maxButtons - 1) {
                    startPage = Math.max(1, endPage - maxButtons + 1);
                  }
                  
                  const pageButtons = [];
                  
                  if (startPage > 1) {
                    pageButtons.push(
                      <button
                        key={1}
                        onClick={() => setPage(1)}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pageButtons.push(
                        <span key="ellipsis1" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      );
                    }
                  }
                  
                  for (let i = startPage; i <= endPage; i++) {
                    pageButtons.push(
                      <button
                        key={i}
                        onClick={() => setPage(i)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          i === page
                            ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }
                  
                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pageButtons.push(
                        <span key="ellipsis2" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      );
                    }
                    pageButtons.push(
                      <button
                        key={totalPages}
                        onClick={() => setPage(totalPages)}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        {totalPages}
                      </button>
                    );
                  }
                  
                  return pageButtons;
                })()}
                
                <button
                  onClick={() => setPage(Math.min(vipDataResponse.pages, page + 1))}
                  disabled={page === vipDataResponse.pages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">다음</span>
                  ▶
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* 메모 모달 */}
      {showMemoModal && selectedWithdrawal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {selectedWithdrawal.status === 'pending' ? '인출 승인 및 메모' : '메모 수정'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  고객용 메모 (고객이 볼 수 있음)
                </label>
                <textarea
                  value={memoForm.memo}
                  onChange={(e) => setMemoForm({ ...memoForm, memo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows="3"
                  placeholder="예: 입금 완료되었습니다."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  관리자용 메모 (관리자만 볼 수 있음)
                </label>
                <textarea
                  value={memoForm.adminMemo}
                  onChange={(e) => setMemoForm({ ...memoForm, adminMemo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows="3"
                  placeholder="예: 2024-01-15 오후 3시 입금 처리"
                />
              </div>
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowMemoModal(false);
                  setSelectedWithdrawal(null);
                  setMemoForm({ memo: '', adminMemo: '' });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                취소
              </button>
              
              {selectedWithdrawal.status === 'pending' ? (
                <button
                  onClick={() => handleWithdrawalAction(
                    selectedWithdrawal.id, 
                    'approve', 
                    '', 
                    memoForm.memo, 
                    memoForm.adminMemo
                  )}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                >
                  승인
                </button>
              ) : (
                <button
                  onClick={() => handleUpdateMemo(selectedWithdrawal.id)}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                >
                  저장
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SystemSettings = () => {
  const [settings, setSettings] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [isInitialized, setIsInitialized] = React.useState(false);

  const { data: systemSettings, isLoading, refetch } = useQuery(
    'admin-settings',
    () => api.get('/admin/settings').then(res => res.data),
    {
      refetchOnWindowFocus: false,
      staleTime: 0,
      cacheTime: 0
    }
  );

  // useEffect로 데이터가 로드되면 settings 상태 업데이트
  React.useEffect(() => {
    if (systemSettings && !isInitialized) {
      console.log('Settings loaded:', systemSettings);
      setSettings(systemSettings);
      setIsInitialized(true);
    }
  }, [systemSettings, isInitialized]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // 공사유형이 비어있으면 빈 문자열로 설정
      const dataToSave = {
        ...settings,
        construction_types: settings.construction_types || ''
      };
      
      console.log('Saving settings:', dataToSave);
      await api.put('/admin/settings', dataToSave);
      alert('설정이 저장되었습니다.');
      
      // 저장 후 데이터 다시 로드
      refetch();
    } catch (error) {
      console.error('설정 저장 실패:', error);
      alert('설정 저장에 실패했습니다: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">로딩 중...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">시스템 설정</h1>
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white px-4 py-3 sm:py-2 rounded-md text-sm font-medium"
        >
          {saving ? '저장 중...' : '설정 저장'}
        </button>
      </div>

      <div className="space-y-4 sm:space-y-6">
        {/* 기본 설정 */}
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">기본 설정</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                취소 프로젝트 고객 차단 기간 (일)
              </label>
              <input
                type="number"
                value={settings.cancelled_project_block_days || 90}
                onChange={(e) => handleChange('cancelled_project_block_days', parseInt(e.target.value))}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                프로젝트 취소 후 고객이 열람 가능한 기간 (기본: 90일)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                기본 VIP 리워드 비율 (%)
              </label>
              <input
                type="number"
                step="0.1"
                value={settings.default_vip_reward_rate || 3.0}
                onChange={(e) => handleChange('default_vip_reward_rate', parseFloat(e.target.value))}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                최소 인출 금액 (원)
              </label>
              <input
                type="number"
                value={settings.min_withdrawal_amount || 100000}
                onChange={(e) => handleChange('min_withdrawal_amount', parseInt(e.target.value))}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
          </div>
        </div>
        
        {/* VIP 추천 기준 설정 */}
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">VIP 추천 기준</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                VIP 추천 프로젝트 횟수
              </label>
              <input
                type="number"
                value={settings.vip_recommendation_project_count || 2}
                onChange={(e) => handleChange('vip_recommendation_project_count', parseInt(e.target.value))}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">프로젝트를 N회 이상 진행한 고객을 VIP로 추천</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                VIP 추천 누적 금액 (원)
              </label>
              <input
                type="number"
                value={settings.vip_recommendation_total_amount || 10000000}
                onChange={(e) => handleChange('vip_recommendation_total_amount', parseInt(e.target.value))}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">누적 계약 금액이 N원 이상인 고객을 VIP로 추천</p>
            </div>
          </div>
          <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>참고:</strong> 두 조건을 모두 만족하는 고객이 VIP 추천 대상이 됩니다.
            </p>
          </div>
        </div>

        {/* 미처리 프로젝트 자동 알림 설정 */}
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">미처리 프로젝트 자동 알림</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                자동 알림 활성화
              </label>
              <select
                value={settings.unprocessed_alert_enabled || 'false'}
                onChange={(e) => handleChange('unprocessed_alert_enabled', e.target.value)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="true">활성화</option>
                <option value="false">비활성화</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                미처리 프로젝트 자동 알림 기능 사용 여부
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                미처리 기준 시간
              </label>
              <select
                value={settings.unprocessed_alert_hours || '1'}
                onChange={(e) => handleChange('unprocessed_alert_hours', e.target.value)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="0.5">30분</option>
                <option value="1">1시간</option>
                <option value="2">2시간</option>
                <option value="3">3시간</option>
                <option value="4">4시간</option>
                <option value="6">6시간</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                프로젝트가 배정 후 이 시간 동안 처리되지 않으면 알림 발송
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                재알림 간격 (분)
              </label>
              <input
                type="number"
                value={settings.unprocessed_alert_interval || 30}
                onChange={(e) => handleChange('unprocessed_alert_interval', parseInt(e.target.value))}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                min="10"
                max="180"
              />
              <p className="mt-1 text-xs text-gray-500">
                첫 알림 후 재알림까지의 대기 시간 (최소 10분)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                최대 알림 횟수
              </label>
              <input
                type="number"
                value={settings.unprocessed_alert_max_retry || 3}
                onChange={(e) => handleChange('unprocessed_alert_max_retry', parseInt(e.target.value))}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                min="1"
                max="10"
              />
              <p className="mt-1 text-xs text-gray-500">
                동일 프로젝트에 대한 최대 알림 발송 횟수
              </p>
            </div>
          </div>
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>알림 방식:</strong> 설정된 시간 이상 '접수' 상태로 머물러 있는 프로젝트의 담당 소장에게 FCM 푸시 알림이 발송됩니다.
              알림음은 '미처리' 타입으로 설정됩니다.
            </p>
          </div>
        </div>

        {/* 자동 로그인 유지 기간 설정 */}
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">자동 로그인 유지 기간</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                관리자 (일)
              </label>
              <input
                type="number"
                value={settings.session_days_admin || 30}
                onChange={(e) => handleChange('session_days_admin', parseInt(e.target.value))}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                min="1"
                max="365"
              />
              <p className="mt-1 text-xs text-gray-500">
                관리자 계정의 자동 로그인 유지 기간 (현재: {settings.session_days_admin || 30}일)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                소장 (일)
              </label>
              <input
                type="number"
                value={settings.session_days_contractor || 365}
                onChange={(e) => handleChange('session_days_contractor', parseInt(e.target.value))}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                min="1"
                max="3650"
              />
              <p className="mt-1 text-xs text-gray-500">
                소장 계정의 자동 로그인 유지 기간 (현재: {settings.session_days_contractor || 365}일)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                일반 고객 (일)
              </label>
              <input
                type="number"
                value={settings.session_days_customer || 120}
                onChange={(e) => handleChange('session_days_customer', parseInt(e.target.value))}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                min="1"
                max="365"
              />
              <p className="mt-1 text-xs text-gray-500">
                일반 고객의 자동 로그인 유지 기간 (현재: {settings.session_days_customer || 120}일)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                VIP 고객 (일)
              </label>
              <input
                type="number"
                value={settings.session_days_vip_customer || 120}
                onChange={(e) => handleChange('session_days_vip_customer', parseInt(e.target.value))}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                min="1"
                max="365"
              />
              <p className="mt-1 text-xs text-gray-500">
                VIP 고객의 자동 로그인 유지 기간 (현재: {settings.session_days_vip_customer || 120}일)
              </p>
            </div>
          </div>
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>참고:</strong> 설정 변경 후 새로 로그인하는 사용자부터 적용됩니다. 기존 로그인 세션은 유지됩니다.
            </p>
          </div>
        </div>

        {/* 공사 유형 설정 */}
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">공사 유형 설정</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              공사 유형 (쉼표로 구분)
            </label>
            <textarea
              rows={3}
              value={settings.construction_types || ''}
              onChange={(e) => handleChange('construction_types', e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="단열공사,방수공사,철거공사,원상복구..."
            />
            <p className="mt-1 text-xs text-gray-500">
              현재 값: {settings.construction_types ? `"${settings.construction_types}"` : '(비어있음)'}
            </p>
          </div>
        </div>

        {/* 관리자 알림 정책 설정 */}
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">관리자 알림 정책</h2>
          <AdminNotificationPolicy 
            settings={settings} 
            handleChange={handleChange}
          />
        </div>

        {/* 파일 업로드 설정 */}
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">파일 업로드 설정</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                최대 이미지 크기 (MB)
              </label>
              <input
                type="number"
                value={settings.max_image_size ? settings.max_image_size / (1024 * 1024) : 10}
                onChange={(e) => handleChange('max_image_size', parseInt(e.target.value) * 1024 * 1024)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                최대 문서 크기 (MB)
              </label>
              <input
                type="number"
                value={settings.max_document_size ? settings.max_document_size / (1024 * 1024) : 50}
                onChange={(e) => handleChange('max_document_size', parseInt(e.target.value) * 1024 * 1024)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
          </div>
        </div>

        {/* FCM 디바이스 정책 설정 */}
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">FCM 푸시 알림 정책</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              디바이스 관리 정책
            </label>
            <select
              value={settings.fcm_device_policy || 'single'}
              onChange={(e) => handleChange('fcm_device_policy', e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="single">단일 디바이스 (마지막 로그인 디바이스만)</option>
              <option value="multiple_recent">다중 디바이스 (30일 이내 사용)</option>
              <option value="multiple_all">다중 디바이스 (모든 디바이스)</option>
              <option value="limited_5">제한된 다중 디바이스 (최대 5개)</option>
            </select>
            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>정책 설명:</strong>
              </p>
              <ul className="mt-1 text-xs text-blue-700 space-y-1">
                <li>• <strong>단일 디바이스:</strong> 가장 최근에 로그인한 디바이스에만 푸시 전송</li>
                <li>• <strong>다중 디바이스 (30일):</strong> 30일 이내 사용한 모든 디바이스에 푸시 전송</li>
                <li>• <strong>다중 디바이스 (전체):</strong> 등록된 모든 디바이스에 푸시 전송</li>
                <li>• <strong>제한된 다중 디바이스:</strong> 최근 사용한 5개 디바이스까지만 푸시 전송</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 검토 기간 초과 자동 취소 설정 */}
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">검토 기간 초과 자동 취소</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                자동 취소 활성화
              </label>
              <select
                value={settings.auto_cancel_enabled || 'false'}
                onChange={(e) => handleChange('auto_cancel_enabled', e.target.value)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="true">활성화</option>
                <option value="false">비활성화</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                검토 상태에서 일정 기간 경과 시 자동 취소 기능
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                경고 알림 일수
              </label>
              <input
                type="number"
                value={settings.auto_cancel_warning_days || 59}
                onChange={(e) => handleChange('auto_cancel_warning_days', parseInt(e.target.value))}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                min="1"
                max="90"
              />
              <p className="mt-1 text-xs text-gray-500">
                검토 상태로 이 기간이 경과하면 경고 알림을 발송합니다
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                자동 취소 기준 일수
              </label>
              <input
                type="number"
                value={settings.auto_cancel_days || 60}
                onChange={(e) => handleChange('auto_cancel_days', parseInt(e.target.value))}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                min="2"
                max="90"
              />
              <p className="mt-1 text-xs text-gray-500">
                검토 상태로 이 기간 동안 머물면 자동으로 취소됩니다
              </p>
            </div>
          </div>
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>알림 순서:</strong>
            </p>
            <ul className="mt-1 text-xs text-blue-700 space-y-1">
              <li>• <strong>{settings.auto_cancel_warning_days || 59}일 경과:</strong> 경고 알림 발송 (곧 취소될 예정)</li>
              <li>• <strong>{settings.auto_cancel_days || 60}일 경과:</strong> 실제 자동 취소 + 취소 알림 발송</li>
            </ul>
          </div>
        </div>

        {/* 알림 설정 */}
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">알림 설정</h2>
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="sms_notifications"
                checked={settings.enable_sms_notifications !== false}
                onChange={(e) => handleChange('enable_sms_notifications', e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="sms_notifications" className="ml-2 block text-sm text-gray-900">
                SMS 알림 활성화
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="email_notifications"
                checked={settings.enable_email_notifications !== false}
                onChange={(e) => handleChange('enable_email_notifications', e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="email_notifications" className="ml-2 block text-sm text-gray-900">
                이메일 알림 활성화
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 소장 생성/수정 모달
const ContractorModal = ({ contractor, onClose, onSave }) => {
  const [formData, setFormData] = React.useState({
    name: contractor?.name || '',
    login_id: contractor?.login_id || '',
    email: contractor?.email || '',
    password: '',
    phone: contractor?.phone || '',
    company_name: contractor?.company_name || '',
    specialties: contractor?.specialties || '',
    admin_notes: contractor?.admin_notes || ''
  });
  const [selectedSpecialties, setSelectedSpecialties] = React.useState(
    contractor?.specialties ? contractor.specialties.split(',').map(s => s.trim()) : []
  );

  // 공사 유형 목록 가져오기
  const { data: settings } = useQuery(
    'contractor-modal-settings',
    () => api.get('/settings').then(res => res.data)
  );

  const constructionTypes = React.useMemo(() => {
    if (!settings?.construction_types) return [];
    return settings.construction_types.split(',').map(type => type.trim());
  }, [settings]);

  const handleSpecialtyToggle = (type) => {
    setSelectedSpecialties(prev => {
      const newSpecialties = prev.includes(type)
        ? prev.filter(s => s !== type)
        : [...prev, type];
      
      // formData도 함께 업데이트
      setFormData(prevForm => ({
        ...prevForm,
        specialties: newSpecialties.join(',')
      }));
      
      return newSpecialties;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      specialties: selectedSpecialties.join(',')
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {contractor ? '소장 정보 수정' : '새 소장 등록'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                소장명
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                로그인 ID
              </label>
              <input
                type="text"
                value={formData.login_id}
                onChange={(e) => handleChange('login_id', e.target.value)}
                required
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                이메일
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="선택사항"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                비밀번호 {contractor && '(비워두면 변경안함)'}
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                required={!contractor}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                연락처
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                업체명
              </label>
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                전문 분야 (복수 선택 가능)
              </label>
              <div className="border border-gray-300 rounded-md p-3 max-h-40 overflow-y-auto">
                <div className="grid grid-cols-2 gap-2">
                  {constructionTypes.map((type) => (
                    <label key={type} className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={selectedSpecialties.includes(type)}
                        onChange={() => handleSpecialtyToggle(type)}
                        className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="truncate" title={type}>{type}</span>
                    </label>
                  ))}
                </div>
              </div>
              {selectedSpecialties.length > 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  선택됨: {selectedSpecialties.join(', ')}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                관리자 메모 (소장에게는 보이지 않음)
              </label>
              <textarea
                value={formData.admin_notes}
                onChange={(e) => handleChange('admin_notes', e.target.value)}
                rows={3}
                placeholder="소장에 대한 내부 메모를 작성하세요..."
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md text-sm font-medium"
              >
                취소
              </button>
              <button
                type="submit"
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                {contractor ? '수정' : '등록'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// VIP 수수료 설정 컴포넌트
const VipFeeSettings = () => {
  const [defaultFeeRate, setDefaultFeeRate] = React.useState(3.3);
  const [isEditing, setIsEditing] = React.useState(false);
  const [tempFeeRate, setTempFeeRate] = React.useState(3.3);

  // 전역 수수료 설정 조회
  const { data: feeSettings, refetch: refetchFeeSettings } = useQuery(
    'vip-fee-settings',
    () => api.get('/vip/fee-settings').then(res => res.data),
    {
      onSuccess: (data) => {
        setDefaultFeeRate(data.defaultFeeRate);
        setTempFeeRate(data.defaultFeeRate);
      }
    }
  );

  // 전역 수수료 설정 업데이트
  const updateFeeSettings = useMutation(
    (feeRate) => api.put('/vip/fee-settings', { defaultFeeRate: feeRate }),
    {
      onSuccess: () => {
        toast.success('VIP 기본 수수료율이 업데이트되었습니다.');
        setDefaultFeeRate(tempFeeRate);
        setIsEditing(false);
        refetchFeeSettings();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '수수료 설정 업데이트에 실패했습니다.');
      }
    }
  );

  const handleSave = () => {
    if (tempFeeRate < 0 || tempFeeRate > 100) {
      toast.error('수수료율은 0% ~ 100% 사이여야 합니다.');
      return;
    }
    updateFeeSettings.mutate(tempFeeRate);
  };

  const handleCancel = () => {
    setTempFeeRate(defaultFeeRate);
    setIsEditing(false);
  };

  return (
    <div className="bg-white shadow rounded-lg p-4 sm:p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-medium text-gray-900">VIP 수수료 설정</h2>
          <p className="text-sm text-gray-500 mt-1">
            VIP 고객 인출 시 적용되는 기본 수수료율을 설정합니다.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!isEditing ? (
            <>
              <div className="text-right">
                <span className="text-sm text-gray-500">현재 기본 수수료율</span>
                <div className="text-2xl font-bold text-indigo-600">{defaultFeeRate}%</div>
              </div>
              <button
                onClick={() => setIsEditing(true)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                수정
              </button>
            </>
          ) : (
            <div className="flex items-center gap-3">
              <div className="flex items-center">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={tempFeeRate}
                  onChange={(e) => setTempFeeRate(parseFloat(e.target.value) || 0)}
                  className="w-20 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                <span className="ml-1 text-sm text-gray-500">%</span>
              </div>
              <button
                onClick={handleCancel}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleSave}
                disabled={updateFeeSettings.isLoading}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {updateFeeSettings.isLoading ? '저장 중...' : '저장'}
              </button>
            </div>
          )}
        </div>
      </div>
      
      <div className="bg-blue-50 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <span className="text-blue-400">ℹ️</span>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">수수료 적용 규칙</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc pl-5 space-y-1">
                <li>개별 VIP 설정이 있으면 개별 설정이 우선 적용됩니다.</li>
                <li>개별 설정이 없으면 이 기본 수수료율이 적용됩니다.</li>
                <li>수수료율이 0%인 경우 수수료가 부과되지 않습니다.</li>
                <li>변경된 수수료율은 새로운 인출 신청부터 적용됩니다.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// VIP 전환 모달
const VipConversionModal = ({ customer, onClose, onConfirm }) => {
  const [rewardRate, setRewardRate] = useState('');
  const { data: settings } = useQuery('admin-settings', () => 
    api.get('/admin/settings').then(res => res.data),
    {
      refetchOnMount: 'always',
      staleTime: 0,
      cacheTime: 0
    }
  );
  
  useEffect(() => {
    if (settings) {
      setRewardRate(settings.default_vip_reward_rate || '3.0');
    }
  }, [settings]);

  const handleConfirm = () => {
    const rate = parseFloat(rewardRate);
    if (isNaN(rate) || rate < 0 || rate > 100) {
      toast.error('리워드 비율은 0~100 사이의 숫자여야 합니다.');
      return;
    }
    onConfirm({ customerId: customer.id, rewardRate: rate });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            VIP 고객 전환 확인
          </h3>
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              다음 고객을 VIP 고객으로 전환하시겠습니까?
            </p>
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="font-medium">{customer.name}</p>
              <p className="text-sm text-gray-600">{customer.phone}</p>
              {customer.project_count > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <p className="text-sm text-gray-700">프로젝트: {customer.project_count}회</p>
                  {customer.total_amount > 0 && (
                    <p className="text-sm text-gray-700">누적 금액: {(customer.total_amount / 10000).toLocaleString()}만원</p>
                  )}
                </div>
              )}
            </div>
            {customer.vip_recommended && (
              <div className="mt-3 p-2 bg-yellow-50 rounded-md">
                <p className="text-sm text-yellow-800 font-medium">
                  ⭐ VIP 추천 대상 고객입니다
                </p>
              </div>
            )}
            
            {/* 리워드 비율 설정 */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                리워드 비율 (%)
              </label>
              <input
                type="number"
                value={rewardRate}
                onChange={(e) => setRewardRate(e.target.value)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                step="0.1"
                min="0"
                max="100"
                placeholder="3.0"
              />
              <p className="mt-1 text-xs text-gray-500">
                완료된 프로젝트 계약금액의 {rewardRate || '0'}%가 적립됩니다
              </p>
            </div>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md text-sm font-medium"
            >
              취소
            </button>
            <button
              onClick={handleConfirm}
              className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              VIP 전환
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 공지사항 관리 컴포넌트
const NoticeManagement = () => {
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25);
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [editingNotice, setEditingNotice] = React.useState(null);
  const [categoryFilter, setCategoryFilter] = React.useState('all');

  const { data: noticesData, isLoading, refetch } = useQuery(
    ['admin-notices', categoryFilter, page, pageSize],
    () => api.get(`/notices?category=${categoryFilter}&page=${page}&limit=${pageSize}`).then(res => res.data),
    { keepPreviousData: true }
  );

  const notices = noticesData?.notices || [];

  const handleCreateNotice = async (data) => {
    try {
      // 디버깅 로그
      console.log('Creating notice with data:', data);
      
      let response;
      // FormData의 경우 headers 설정
      if (data instanceof FormData) {
        // FormData 내용 로그
        for (let [key, value] of data.entries()) {
          console.log('FormData key:', key, 'value:', value);
        }
        
        response = await api.post('/notices', data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        response = await api.post('/notices', data);
      }
      
      console.log('Notice created successfully:', response.data);
      
      // 성공 시에만 모달 닫기
      setShowCreateModal(false);
      toast.success('공지사항이 등록되었습니다.');
      
      // 데이터 새로고침
      setTimeout(() => {
        refetch();
      }, 500);
      
    } catch (error) {
      console.error('공지사항 생성 실패 - 전체 에러:', error);
      console.error('에러 응답:', error.response?.data);
      
      // 구체적인 에러 메시지 표시
      const errorMessage = error.response?.data?.error || error.message || '공지사항 생성에 실패했습니다.';
      toast.error(errorMessage);
      
      // 에러 발생 시 모달은 열어둠
    }
  };

  const handleUpdateNotice = async (id, data) => {
    try {
      // FormData의 경우 headers 설정
      if (data instanceof FormData) {
        await api.put(`/notices/${id}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await api.put(`/notices/${id}`, data);
      }
      refetch();
      setEditingNotice(null);
      toast.success('공지사항이 수정되었습니다.');
    } catch (error) {
      console.error('공지사항 수정 실패:', error);
      toast.error('공지사항 수정에 실패했습니다.');
    }
  };

  const handleDeleteNotice = async (id) => {
    if (window.confirm('정말로 이 공지사항을 삭제하시겠습니까?')) {
      try {
        await api.delete(`/notices/${id}`);
        refetch();
      } catch (error) {
        console.error('공지사항 삭제 실패:', error);
      }
    }
  };

  const targetAudienceLabels = {
    all: '전체',
    contractor: '소장',
    customer: '고객',
    vip_customer: 'VIP 고객'
  };

  const getTargetAudienceLabel = (target) => {
    if (!target) return '전체';
    
    if (target === 'individual') {
      return '개별 공지';
    }
    
    const targets = target.split(',');
    const labels = targets.map(t => targetAudienceLabels[t.trim()] || t.trim());
    return labels.join(', ');
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl lg:text-2xl font-semibold text-gray-900">공지사항 관리</h1>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">표시 개수:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value={25}>25개</option>
              <option value={50}>50개</option>
              <option value={100}>100개</option>
              <option value={200}>200개</option>
            </select>
          </div>
          <div className="flex gap-2">
            <Link
              to="/notices"
              className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap"
            >
              공지사항 보기
            </Link>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap"
            >
              새 공지사항
            </button>
          </div>
        </div>
      </div>

      {/* 카테고리 필터 */}
      <div className="bg-white shadow rounded-lg p-4 lg:p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <label className="text-sm font-medium text-gray-700">카테고리 필터:</label>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="block w-full lg:w-48 px-3 py-2 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
          >
            <option value="all">전체 공지</option>
            <option value="contractor">소장 공지</option>
            <option value="customer">고객 공지</option>
            <option value="vip_customer">VIP 고객 공지</option>
          </select>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">로딩 중...</p>
          </div>
        ) : notices.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">등록된 공지사항이 없습니다.</p>
          </div>
        ) : (
          <>
            {/* 모바일/태블릿: 카드 레이아웃 */}
            <div className="xl:hidden space-y-3 p-4">
              {notices.map((notice) => (
                <div key={notice.id} className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-sm font-semibold text-gray-900 flex-1">{notice.title}</h3>
                    {notice.is_important && (
                      <span className="ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                        중요
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                    <span className={`inline-flex px-2 py-0.5 text-xs rounded-full font-medium ${
                      notice.target_audience === 'individual' 
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-sm' 
                        : 'bg-blue-50 text-blue-700'
                    }`}>
                      {notice.target_audience === 'individual' ? '🔒 개별 공지' : getTargetAudienceLabel(notice.target_audience)}
                    </span>
                    <span>조회 {notice.view_count || 0}</span>
                    <span>{new Date(notice.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingNotice(notice)}
                      className="flex-1 text-center px-3 py-1.5 bg-indigo-50 text-indigo-700 text-xs font-medium rounded hover:bg-indigo-100"
                    >
                      수정
                    </button>
                    <button
                      onClick={() => handleDeleteNotice(notice.id)}
                      className="flex-1 text-center px-3 py-1.5 bg-red-50 text-red-700 text-xs font-medium rounded hover:bg-red-100"
                    >
                      삭제
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            {/* PC: 테이블 레이아웃 */}
            <div className="hidden xl:block">
              <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    제목
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    대상
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    중요도
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    조회수
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    작성일
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    액션
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {notices.map((notice) => (
                  <tr key={notice.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {notice.title}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        notice.target_audience === 'individual' 
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-sm' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {notice.target_audience === 'individual' ? '🔒 개별 공지' : getTargetAudienceLabel(notice.target_audience)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {notice.is_important ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          중요
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                          일반
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {notice.view_count || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(notice.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => setEditingNotice(notice)}
                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                      >
                        수정
                      </button>
                      <button
                        onClick={() => handleDeleteNotice(notice.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* 페이지네이션 */}
      {noticesData && noticesData.pages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              이전
            </button>
            <button
              onClick={() => setPage(Math.min(noticesData.pages, page + 1))}
              disabled={page === noticesData.pages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              다음
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                전체 <span className="font-medium">{noticesData.total}</span>개 중{' '}
                <span className="font-medium">{((page - 1) * pageSize) + 1}</span> -{' '}
                <span className="font-medium">{Math.min(page * pageSize, noticesData.total)}</span> 표시
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">이전</span>
                  ◀
                </button>
                
                {(() => {
                  const totalPages = noticesData.pages;
                  const maxButtons = 7;
                  let startPage = Math.max(1, page - Math.floor(maxButtons / 2));
                  let endPage = Math.min(totalPages, startPage + maxButtons - 1);
                  
                  if (endPage - startPage < maxButtons - 1) {
                    startPage = Math.max(1, endPage - maxButtons + 1);
                  }
                  
                  const pageButtons = [];
                  
                  if (startPage > 1) {
                    pageButtons.push(
                      <button
                        key={1}
                        onClick={() => setPage(1)}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pageButtons.push(
                        <span key="ellipsis1" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      );
                    }
                  }
                  
                  for (let i = startPage; i <= endPage; i++) {
                    pageButtons.push(
                      <button
                        key={i}
                        onClick={() => setPage(i)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          i === page
                            ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }
                  
                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pageButtons.push(
                        <span key="ellipsis2" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      );
                    }
                    pageButtons.push(
                      <button
                        key={totalPages}
                        onClick={() => setPage(totalPages)}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        {totalPages}
                      </button>
                    );
                  }
                  
                  return pageButtons;
                })()}
                
                <button
                  onClick={() => setPage(Math.min(noticesData.pages, page + 1))}
                  disabled={page === noticesData.pages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">다음</span>
                  ▶
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* 공지사항 생성 모달 */}
      {showCreateModal && (
        <NoticeModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateNotice}
        />
      )}

      {/* 공지사항 수정 모달 */}
      {editingNotice && (
        <NoticeModal
          notice={editingNotice}
          onClose={() => setEditingNotice(null)}
          onSave={(data) => handleUpdateNotice(editingNotice.id, data)}
        />
      )}
    </div>
  );
};

// 공지사항 생성/수정 모달
const NoticeModal = ({ notice, onClose, onSave }) => {
  const [formData, setFormData] = React.useState({
    title: notice?.title || '',
    content: notice?.content || '',
    target_audience: notice?.target_audience ? notice.target_audience.split(',') : ['all'],
    is_important: notice?.is_important || false
  });
  const [uploadedImages, setUploadedImages] = React.useState([]);
  const [attachedFiles, setAttachedFiles] = React.useState([]);
  const [existingAttachments, setExistingAttachments] = React.useState([]);
  const contentRef = React.useRef(null);
  
  // 개별 사용자 선택 관련 상태
  const [showIndividualSearch, setShowIndividualSearch] = React.useState(false);
  const [individualSearchQuery, setIndividualSearchQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState([]);
  const [selectedUsers, setSelectedUsers] = React.useState([]);
  const [isSearching, setIsSearching] = React.useState(false);

  // 수정 모드일 때 기존 첨부파일 및 개별 사용자 로드
  React.useEffect(() => {
    if (notice?.id) {
      api.get(`/notices/${notice.id}`)
        .then(res => {
          if (res.data.attachments) {
            setExistingAttachments(res.data.attachments);
          }
          // 개별 사용자 공지인 경우
          if (res.data.target_audience === 'individual' && res.data.individual_users) {
            setShowIndividualSearch(true);
            setSelectedUsers(res.data.individual_users);
            setFormData(prev => ({ ...prev, target_audience: [] }));
          }
        })
        .catch(err => console.error('Failed to load notice details:', err));
    }
  }, [notice?.id]);
  
  // 사용자 검색
  const searchUsers = React.useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await api.get('/admin/users/search', {
        params: { 
          query,
          limit: 20
        }
      });
      setSearchResults(response.data.users || []);
    } catch (error) {
      console.error('User search error:', error);
      toast.error('사용자 검색에 실패했습니다.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);
  
  // 검색 디바운싱
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (showIndividualSearch) {
        searchUsers(individualSearchQuery);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [individualSearchQuery, showIndividualSearch, searchUsers]);
  
  // 사용자 선택/해제
  const toggleUserSelection = (user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u.id === user.id);
      if (isSelected) {
        return prev.filter(u => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };
  
  // 선택된 사용자 제거
  const removeSelectedUser = (userId) => {
    setSelectedUsers(prev => prev.filter(u => u.id !== userId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // FormData 생성
    const submitData = new FormData();
    submitData.append('title', formData.title);
    submitData.append('content', formData.content);
    
    // 개별 사용자가 선택된 경우
    if (showIndividualSearch && selectedUsers.length > 0) {
      console.log('Submitting individual notice for users:', selectedUsers);
      submitData.append('target_audience', 'individual');
      submitData.append('individual_users', JSON.stringify(selectedUsers.map(u => u.id)));
      
      // 디버깅용 로그
      console.log('FormData individual_users:', JSON.stringify(selectedUsers.map(u => u.id)));
    } else {
      submitData.append('target_audience', formData.target_audience.join(','));
    }
    
    submitData.append('is_important', formData.is_important);
    
    // 첨부파일 추가
    attachedFiles.forEach((file) => {
      submitData.append('attachments', file);
    });
    
    // FormData 내용 확인
    for (let [key, value] of submitData.entries()) {
      console.log('FormData:', key, value);
    }
    
    onSave(submitData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // 이미지 업로드 처리
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await api.post('/notices/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const imageUrl = response.data.url;
      setUploadedImages(prev => [...prev, imageUrl]);
      
      // 커서 위치에 이미지 삽입
      insertImageAtCursor(imageUrl);
    } catch (error) {
      console.error('이미지 업로드 실패:', error);
      toast.error('이미지 업로드에 실패했습니다.');
    }
  };

  // 커서 위치에 이미지 마크다운 삽입
  const insertImageAtCursor = (imageUrl) => {
    const textarea = contentRef.current;
    const cursorPos = textarea.selectionStart;
    const textBefore = formData.content.substring(0, cursorPos);
    const textAfter = formData.content.substring(cursorPos);
    
    const imageMarkdown = `\n![이미지](${imageUrl})\n`;
    
    setFormData(prev => ({
      ...prev,
      content: textBefore + imageMarkdown + textAfter
    }));
    
    // 커서를 이미지 마크다운 뒤로 이동
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        cursorPos + imageMarkdown.length,
        cursorPos + imageMarkdown.length
      );
    }, 0);
  };

  // 유튜브 영상 삽입
  const insertYoutubeVideo = () => {
    const url = prompt('유튜브 URL을 입력하세요:');
    if (!url) return;
    
    let videoId = '';
    let isShorts = false;
    
    // 유튜브 URL에서 비디오 ID 추출 및 쇼츠 여부 확인
    const patterns = [
      { regex: /youtube\.com\/shorts\/([^&\n?#\/]+)/, isShorts: true },
      { regex: /youtu\.be\/([^&\n?#\/]+)/, isShorts: false },
      { regex: /youtube\.com\/embed\/([^&\n?#\/]+)/, isShorts: false },
      { regex: /youtube\.com\/watch\?v=([^&\n?#\/]+)/, isShorts: false },
      { regex: /youtube\.com\/watch\?.*v=([^&\n?#\/]+)/, isShorts: false }
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern.regex);
      if (match) {
        videoId = match[1];
        isShorts = pattern.isShorts;
        break;
      }
    }
    
    if (!videoId) {
      toast.error('올바른 유튜브 URL이 아닙니다.');
      return;
    }
    
    // 유튜브 iframe 마크다운 삽입 (쇼츠/일반 구분)
    const textarea = contentRef.current;
    const cursorPos = textarea.selectionStart;
    const textBefore = formData.content.substring(0, cursorPos);
    const textAfter = formData.content.substring(cursorPos);
    
    const containerClass = isShorts ? 'youtube-shorts-container' : 'youtube-video-container';
    const youtubeMarkdown = `\n<div class="${containerClass}">\n  <iframe src="https://www.youtube.com/embed/${videoId}" frameborder="0" allowfullscreen></iframe>\n</div>\n`;
    
    setFormData(prev => ({
      ...prev,
      content: textBefore + youtubeMarkdown + textAfter
    }));
    
    // 커서를 유튜브 마크다운 뒤로 이동
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        cursorPos + youtubeMarkdown.length,
        cursorPos + youtubeMarkdown.length
      );
    }, 0);
  };

  // 파일 첨부 처리
  const handleFileAttachment = (e) => {
    const files = Array.from(e.target.files);
    setAttachedFiles(prev => [...prev, ...files]);
  };

  // 첨부파일 제거
  const removeAttachment = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // 기존 첨부파일 삭제
  const removeExistingAttachment = async (attachmentId) => {
    if (!notice?.id) return;
    
    if (window.confirm('이 첨부파일을 삭제하시겠습니까?')) {
      try {
        await api.delete(`/notices/${notice.id}/attachments/${attachmentId}`);
        setExistingAttachments(prev => prev.filter(att => att.id !== attachmentId));
        toast.success('첨부파일이 삭제되었습니다.');
      } catch (error) {
        console.error('첨부파일 삭제 실패:', error);
        toast.error('첨부파일 삭제에 실패했습니다.');
      }
    }
  };

  const handleTargetAudienceChange = (targetType, isChecked) => {
    if (targetType === 'all') {
      // 전체 선택 시 다른 모든 선택 해제
      setFormData(prev => ({ ...prev, target_audience: isChecked ? ['all'] : [] }));
    } else {
      setFormData(prev => {
        let newTargets = [...prev.target_audience];
        
        if (isChecked) {
          // 'all'이 선택되어 있으면 제거
          newTargets = newTargets.filter(t => t !== 'all');
          newTargets.push(targetType);
        } else {
          newTargets = newTargets.filter(t => t !== targetType);
        }
        
        // 아무것도 선택되지 않았으면 전체 선택
        if (newTargets.length === 0) {
          newTargets = ['all'];
        }
        
        return { ...prev, target_audience: newTargets };
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* 헤더 - 고정 */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold">
              {notice ? '공지사항 수정' : '새 공지사항 등록'}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
        
        {/* 콘텐츠 - 스크롤 가능 */}
        <div className="p-6 overflow-y-auto flex-1">
          <form onSubmit={handleSubmit} className="space-y-4" id="notice-form">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                제목 *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                required
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="공지사항 제목을 입력하세요"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                대상 * (복수 선택 가능)
              </label>
              
              {/* 공지 유형 선택 */}
              <div className="mb-3 flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowIndividualSearch(false);
                    setSelectedUsers([]);
                    setIndividualSearchQuery('');
                  }}
                  className={`px-3 py-1.5 text-sm rounded-md ${
                    !showIndividualSearch 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  그룹 공지
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowIndividualSearch(true);
                    setFormData(prev => ({ ...prev, target_audience: [] }));
                  }}
                  className={`px-3 py-1.5 text-sm rounded-md ${
                    showIndividualSearch 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  개별 공지
                </button>
              </div>
              
              {/* 그룹 선택 */}
              {!showIndividualSearch && (
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.target_audience.includes('all')}
                      onChange={(e) => handleTargetAudienceChange('all', e.target.checked)}
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">전체</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.target_audience.includes('contractor')}
                      onChange={(e) => handleTargetAudienceChange('contractor', e.target.checked)}
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">소장</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.target_audience.includes('customer')}
                      onChange={(e) => handleTargetAudienceChange('customer', e.target.checked)}
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">고객</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.target_audience.includes('vip_customer')}
                      onChange={(e) => handleTargetAudienceChange('vip_customer', e.target.checked)}
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">VIP 고객</span>
                  </label>
                </div>
              )}
              
              {/* 개별 사용자 검색 */}
              {showIndividualSearch && (
                <div className="space-y-3">
                  <div className="relative">
                    <input
                      type="text"
                      value={individualSearchQuery}
                      onChange={(e) => setIndividualSearchQuery(e.target.value)}
                      placeholder="이름, 전화번호, 이메일로 검색"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    {isSearching && (
                      <div className="absolute right-3 top-3">
                        <div className="animate-spin h-4 w-4 border-2 border-indigo-600 border-t-transparent rounded-full"></div>
                      </div>
                    )}
                  </div>
                  
                  {/* 검색 결과 */}
                  {searchResults.length > 0 && (
                    <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md">
                      {searchResults.map(user => (
                        <div
                          key={user.id}
                          onClick={() => toggleUserSelection(user)}
                          className={`px-3 py-2 cursor-pointer hover:bg-gray-50 ${
                            selectedUsers.some(u => u.id === user.id) ? 'bg-indigo-50' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">{user.name}</span>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  user.user_type === 'contractor' ? 'bg-blue-100 text-blue-800' :
                                  user.user_type === 'vip_customer' ? 'bg-purple-100 text-purple-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {user.user_type === 'contractor' ? '소장' : 
                                   user.user_type === 'vip_customer' ? 'VIP' : '고객'}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500">
                                {user.phone} {user.company_name && `| ${user.company_name}`}
                              </div>
                            </div>
                            {selectedUsers.some(u => u.id === user.id) && (
                              <CheckCircleIcon className="h-5 w-5 text-indigo-600" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* 선택된 사용자 목록 */}
                  {selectedUsers.length > 0 && (
                    <div className="mt-3">
                      <div className="text-xs text-gray-600 mb-1">선택된 사용자 ({selectedUsers.length}명)</div>
                      <div className="flex flex-wrap gap-2">
                        {selectedUsers.map(user => (
                          <span
                            key={user.id}
                            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                          >
                            {user.name} 
                            <span className="ml-1 text-indigo-600">
                              ({user.user_type === 'contractor' ? '소장' : 
                                user.user_type === 'vip_customer' ? 'VIP' : '고객'})
                            </span>
                            <button
                              type="button"
                              onClick={() => removeSelectedUser(user.id)}
                              className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-indigo-200"
                            >
                              <XMarkIcon className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                내용 *
              </label>
              <div className="mb-2 flex gap-2">
                <label className="inline-flex items-center px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-md cursor-pointer">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  이미지 삽입
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </label>
                <button
                  type="button"
                  onClick={insertYoutubeVideo}
                  className="inline-flex items-center px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-md"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  유튜브 삽입
                </button>
                <div className="text-xs text-gray-500 flex items-center">
                  본문에 이미지나 유튜브 영상을 삽입하려면 클릭하세요
                </div>
              </div>
              <textarea
                ref={contentRef}
                rows={8}
                value={formData.content}
                onChange={(e) => handleChange('content', e.target.value)}
                required
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm font-mono"
                placeholder="공지사항 내용을 입력하세요&#10;&#10;이미지는 ![이미지](URL) 형식으로 표시됩니다"
              />
              <div className="mt-1 text-xs text-gray-500">
                마크다운 문법을 지원합니다. **굵게**, *기울임*, [링크](URL) 등
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                첨부파일
              </label>
              <div className="flex items-center">
                <label className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  파일 첨부
                  <input
                    type="file"
                    multiple
                    onChange={handleFileAttachment}
                    className="hidden"
                  />
                </label>
                <span className="ml-3 text-sm text-gray-500">
                  문서, PDF, 이미지 등을 첨부할 수 있습니다
                </span>
              </div>
              {attachedFiles.length > 0 && (
                <div className="mt-3 space-y-2">
                  {attachedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                      <div className="flex items-center text-sm">
                        <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="text-gray-700">{file.name}</span>
                        <span className="ml-2 text-gray-500">({(file.size / 1024 / 1024).toFixed(2)}MB)</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 기존 첨부파일 표시 (수정 모드) */}
            {existingAttachments.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  기존 첨부파일
                </label>
                <div className="bg-gray-50 rounded-md p-3 space-y-2">
                  {existingAttachments.map((file) => (
                    <div key={file.id} className="flex items-center justify-between bg-white p-2 rounded border border-gray-200">
                      <div className="flex items-center">
                        {file.type?.startsWith('image/') ? (
                          <PhotoIcon className="h-5 w-5 text-blue-500 mr-2" />
                        ) : (
                          <DocumentIcon className="h-5 w-5 text-gray-500 mr-2" />
                        )}
                        <span className="text-sm text-gray-900">{file.name}</span>
                        <span className="text-xs text-gray-500 ml-2">
                          ({file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : '크기 미상'})
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          다운로드
                        </a>
                        <button
                          type="button"
                          onClick={() => removeExistingAttachment(file.id)}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_important"
                checked={formData.is_important}
                onChange={(e) => handleChange('is_important', e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="is_important" className="ml-2 block text-sm text-gray-900">
                중요 공지사항
              </label>
            </div>

          </form>
        </div>
        
        {/* 푸터 - 고정 */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
            >
              취소
            </button>
            <button
              type="submit"
              form="notice-form"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {notice ? '수정' : '등록'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 관리자 알림 정책 컴포넌트
const AdminNotificationPolicy = ({ settings, handleChange }) => {
  const [adminList, setAdminList] = React.useState([]);
  
  // 관리자 목록 조회
  React.useEffect(() => {
    console.log('[AdminNotificationPolicy] Loading admin list...');
    api.get('/users/admins')
      .then(res => {
        console.log('[AdminNotificationPolicy] Admin list loaded:', res.data);
        setAdminList(res.data);
      })
      .catch(err => {
        console.error('[AdminNotificationPolicy] Failed to load admin list:', err);
        console.error('[AdminNotificationPolicy] Error details:', err.response?.data || err.message);
      });
  }, []);

  const handlePolicyChange = (method, field, value) => {
    const policyKey = `notification_policy_${method}`;
    const currentPolicy = settings[policyKey] || { admin_policy: 'all', designated_admin_ids: [] };
    
    const updatedPolicy = {
      ...currentPolicy,
      [field]: value
    };
    
    handleChange(policyKey, updatedPolicy);
  };

  const handleAdminToggle = (method, adminId) => {
    const policyKey = `notification_policy_${method}`;
    const currentPolicy = settings[policyKey] || { admin_policy: 'all', designated_admin_ids: [] };
    
    let newIds = [...(currentPolicy.designated_admin_ids || [])];
    if (newIds.includes(adminId)) {
      newIds = newIds.filter(id => id !== adminId);
    } else {
      newIds.push(adminId);
    }
    
    handlePolicyChange(method, 'designated_admin_ids', newIds);
  };

  const methods = [
    { key: 'sms', label: 'SMS', icon: '📱' },
    { key: 'email', label: '이메일', icon: '✉️' },
    { key: 'push', label: '푸시', icon: '🔔' }
  ];

  return (
    <div className="space-y-6">
      {methods.map(method => {
        const policy = settings[`notification_policy_${method.key}`] || { admin_policy: 'all', designated_admin_ids: [] };
        
        return (
          <div key={method.key} className="border rounded-lg p-4">
            <h3 className="text-base font-medium text-gray-900 mb-3 flex items-center gap-2">
              <span>{method.icon}</span>
              {method.label} 알림 정책
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">관리자 알림 방식</label>
                <select
                  value={policy.admin_policy || 'all'}
                  onChange={(e) => handlePolicyChange(method.key, 'admin_policy', e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="all">모든 관리자에게 알림</option>
                  <option value="designated">지정된 관리자만 알림</option>
                </select>
              </div>
              
              {policy.admin_policy === 'designated' && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">알림 받을 관리자 선택</label>
                  <div className="space-y-2">
                    {adminList.map(admin => (
                      <label key={admin.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={(policy.designated_admin_ids || []).includes(admin.id)}
                          onChange={() => handleAdminToggle(method.key, admin.id)}
                          className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          {admin.name} ({admin.login_id || admin.email})
                        </span>
                      </label>
                    ))}
                  </div>
                  {adminList.length === 0 && (
                    <p className="text-sm text-gray-500 italic">관리자 목록을 불러오는 중...</p>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
      
      <div className="bg-blue-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">📌 알림 정책 설명</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>모든 관리자에게 알림</strong>: 등록된 모든 관리자가 알림을 받습니다.</li>
          <li>• <strong>지정된 관리자만 알림</strong>: 선택한 관리자만 알림을 받습니다.</li>
          <li>• 각 알림 채널(SMS, 이메일, 푸시)별로 다른 정책을 설정할 수 있습니다.</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminDashboard;